import { useCfoPresentationEnrichment } from './cfo/useCfoPresentationEnrichment'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Sparkles } from 'lucide-react'
import type { AppWorkerClient } from '../../data/AppWorkerClient'
import type {
  CfoHomeResponse,
  CfoCurrentStatementMetadata,
  CfoInsightsResponse,
  InsightCard,
  InsightDrillTarget,
  MetricComparison,
  MetricComparisonKind,
  MetricComparisonResponse,
  MetricComparisonRow,
  QuickBooksFpaCacheTablePayload,
  QuickBooksCfoPresentationResponse,
  ServerApiClient,
} from '../../data/ServerApiClient'
import { CfoCurrentSourceNotice } from './cfo/CfoCurrentSourceNotice'
import { type CfoAgentSessionResolver } from '../../ai/cfoAgent'
import { markCfoArtifactReady } from '../../ai/cfoBriefToolHandlers'
import { makeAnalysisSessionKeyV1 } from '../../ai/session'
import { buildTabExecutionScopeId, getBrowserTabId } from '../../data/browserTabExecutionIdentity'
import { QUICKBOOKS_GL_CREATOR_TABLE } from './quickBooksGlCreatorModel'
import {
  AiFeedbackControl,
  AnalyticsProvider,
  ReportProblemButton,
  newAiTraceId,
  useAnalytics,
} from '../../analytics'
import { JourneyShell, getJourneyDefinition } from './journeys'
import { serverWaterfallBridge } from './QuickBooksCfoPresentationExport'
import { CfoPageShell, type CfoShellSelectOption } from './cfo/CfoPageShell'
import type { CfoNavProfile } from './cfo/cfoNav'
import { NAV_DRILL_TARGETS } from './cfo/cfoNav'
import { WaterfallBridge } from './cfo/charts'
import { CFO_SURFACES, cfoInsightSurface } from './cfo/cfoTelemetry'
import { formatCurrencyCompact, normalizeCurrencyTextCompact } from './cfo/cfoFormat'
import { groundingFromInsightCard, ratioHealthVisualFromInsightCard } from './cfo/cfoRatioHealthVisual'
import { explainerForCard, formatByUnit, healthBandFromCard } from './cfo/cfoMetricExplainer'
import { HealthRangeBar } from './cfo/charts/HealthRangeBar'
import { prefetchQuickBooksRatios } from './cfo/cfoRatiosCache' // prewarm ratio library for instant modal cards
import type { CfoRuntimeProfile } from './cfo/cfoRuntimeProfile'
import {
  CfoExplainableValue,
  CfoInteractiveChart,
  useCfoAnalysis,
  useRegisterCfoAnalysisPage,
} from './cfo/analysis'
import { useCfoInlineGlDetail } from './cfo/CfoGlDetailModal'
import { selectBriefHighlights } from './cfo/visuals/briefHighlights'
import { KeyHighlightsSection } from './cfo/visuals/KeyHighlightsSection'
import './CfoHomeBrief.css'

const CFO_HOME_SUGGESTED_PROMPTS = [
  'Summarize this month as a brief I can read aloud.',
  'What changed most this month, and what should I do about it?',
  'Compare the last 3 months vs the prior 3.',
]

type BridgeComparisonOption = {
  kind: MetricComparisonKind
  label: string
  shortLabel: string
  phrase: string
}

const BRIDGE_COMPARISON_OPTIONS: BridgeComparisonOption[] = [
  { kind: 'mom', label: 'vs. prior month', shortLabel: 'MoM', phrase: 'month-over-month' },
  { kind: 'qoq', label: 'vs. prior quarter', shortLabel: 'QoQ', phrase: 'quarter-over-quarter' },
  { kind: 'yoy', label: 'vs. prior year', shortLabel: 'YoY', phrase: 'year-over-year' },
  { kind: 'l3m_vs_prior3m', label: 'last 3mo vs prior 3mo', shortLabel: 'Last 3mo', phrase: 'last 3 months vs the prior 3 months' },
  { kind: 'l12m_vs_prior12m', label: 'last 12mo vs prior 12mo', shortLabel: 'Last 12mo', phrase: 'last 12 months vs the prior 12 months' },
]

const bridgeComparisonOption = (kind: MetricComparisonKind): BridgeComparisonOption =>
  BRIDGE_COMPARISON_OPTIONS.find((option) => option.kind === kind) || BRIDGE_COMPARISON_OPTIONS[0]

// Map a net-income bridge bar to its OWN canonical metric + a focused, metric-
// specific seed, so clicking "COGS" analyzes cost of goods sold (not net income).
type BridgeDriverMeta = { metricId: string; label: string; seedPrompt: string; evidenceSub: string }
const bridgeDriverSeedPrompt = (label: string): string =>
  `Explain the selected ${label} waterfall bar for the selected comparison window. Use the local CFO artifact catalog and SQL evidence to show why this metric changed one layer deeper than the waterfall, then stop before transaction-level detail unless I ask for it.`
function resolveBridgeDriver(driverLabel: string): BridgeDriverMeta {
  const d = driverLabel.toLowerCase()
  if (d.includes('cogs') || d.includes('cost')) {
    return {
      metricId: 'cost_of_sales',
      label: 'Cost of goods sold (COGS)',
      evidenceSub: 'COGS = revenue − gross profit · contribution to the selected net-income bridge change',
      seedPrompt: bridgeDriverSeedPrompt('Cost of Goods Sold (COGS)'),
    }
  }
  if (d.includes('revenue') || d.includes('sales') || d.includes('income')) {
    return {
      metricId: 'revenue',
      label: 'Revenue',
      evidenceSub: 'Contribution to the selected net-income bridge change',
      seedPrompt: bridgeDriverSeedPrompt('Revenue'),
    }
  }
  if (d.includes('opex') || d.includes('operating')) {
    return {
      metricId: 'operating_expenses',
      label: 'Operating expenses',
      evidenceSub: 'Contribution to the selected net-income bridge change',
      seedPrompt: bridgeDriverSeedPrompt('Operating expenses'),
    }
  }
  if (d.includes('gross')) {
    return {
      metricId: 'gross_profit',
      label: 'Gross profit',
      evidenceSub: 'Contribution to the selected net-income bridge change',
      seedPrompt: bridgeDriverSeedPrompt('Gross profit'),
    }
  }
  return {
    metricId: 'net_income',
    label: driverLabel,
    evidenceSub: 'Contribution to the selected net-income bridge change',
    seedPrompt: bridgeDriverSeedPrompt(driverLabel),
  }
}

// CFO Core's browser-DuckDB dataset id. Mirrors `CFO_CORE_DUCKDB_DATASET_ID` in
// AppWorkerClient/worker and the `persistentDatasetId` the connector dialog sets
// for `QuickBooksCfoCore:*` callers; the AI session binds to this dataset so the
// analytic tools query the live CFO Core tables.
const CFO_CORE_DUCKDB_DATASET_ID = '__fg_cfo_core_duckdb__'

const CFO_HOME_WARM_HOME_ARTIFACTS_ON_LOAD = false
const CFO_HOME_WARM_AI_ARTIFACTS_ON_LOAD = false

type CfoHomeWarmArtifactPhase = 'home' | 'ai'
type CfoHomeWarmArtifactStatus = 'pending' | 'downloading' | 'ready' | 'failed'

type CfoHomeWarmArtifactProgress = {
  id: string
  label: string
  phase: CfoHomeWarmArtifactPhase
  status: CfoHomeWarmArtifactStatus
  rows: number
  bytes: number
  elapsed_ms?: number
}

type CfoHomeWarmState = {
  status: 'idle' | 'warming' | 'home_ready' | 'ready' | 'failed'
  artifacts: CfoHomeWarmArtifactProgress[]
  home_ready_ms: number | null
  all_ready_ms: number | null
  current_artifact: string | null
  error: string | null
}

const CFO_HOME_VISIBLE_ARTIFACT_IDS = new Set([
  'cfo_statement_render_bundle',
  'cfo_metric_comparisons',
  'cfo_insights',
  'cfo_statement_monthly_fact',
])

const CFO_HOME_ARTIFACT_LABELS: Record<string, string> = {
  account: 'Accounts',
  trial_balance_monthly: 'Trial balance',
  gl_detail: 'GL detail',
  ai_semantic_views: 'AI semantic views',
  ai_gl_view: 'AI GL view',
  ar_support_detail: 'A/R support detail',
  ar_collectability_detail: 'A/R collectability',
  growth_invoice_metrics_summary: 'Growth summary',
  ar_analysis: 'A/R analysis',
  cfo_statement_monthly_fact: 'Statement facts',
  cfo_statement_render_bundle: 'Statement bundle',
  cfo_metric_comparisons: 'Metric comparisons',
  cfo_ratio_analysis: 'Ratios',
  cfo_insights: 'Insights',
  cfo_valuation_inputs: 'Valuation inputs',
  cfo_valuation_history: 'Valuation history',
  layout_node: 'Layout',
  adjustment_line: 'Adjustments',
}

const CFO_HOME_AI_WARM_PRIORITY: Record<string, number> = {
  cfo_ratio_analysis: 10,
  cfo_valuation_inputs: 11,
  cfo_valuation_history: 11,
  account: 12,
  trial_balance_monthly: 13,
  growth_invoice_metrics_summary: 14,
  ar_analysis: 15,
  layout_node: 16,
  adjustment_line: 17,
  gl_detail: 50,
  ar_collectability_detail: 60,
  ar_support_detail: 61,
}

const EMPTY_WARM_STATE: CfoHomeWarmState = {
  status: 'idle',
  artifacts: [],
  home_ready_ms: null,
  all_ready_ms: null,
  current_artifact: null,
  error: null,
}

type CfoHomeWarmSubscriber = (state: CfoHomeWarmState) => void

type CfoHomeWarmRegistryEntry = {
  state: CfoHomeWarmState
  promise: Promise<void> | null
  subscribers: Set<CfoHomeWarmSubscriber>
}

const cfoHomeWarmRegistry = new Map<string, CfoHomeWarmRegistryEntry>()

const cloneWarmState = (state: CfoHomeWarmState): CfoHomeWarmState => ({
  ...state,
  artifacts: state.artifacts.map((artifact) => ({ ...artifact })),
})

const getCfoHomeWarmEntry = (key: string): CfoHomeWarmRegistryEntry => {
  const existing = cfoHomeWarmRegistry.get(key)
  if (existing) return existing
  const entry: CfoHomeWarmRegistryEntry = {
    state: cloneWarmState(EMPTY_WARM_STATE),
    promise: null,
    subscribers: new Set(),
  }
  cfoHomeWarmRegistry.set(key, entry)
  return entry
}

const setCfoHomeWarmEntryState = (entry: CfoHomeWarmRegistryEntry, state: CfoHomeWarmState) => {
  entry.state = cloneWarmState(state)
  entry.subscribers.forEach((subscriber) => subscriber(cloneWarmState(entry.state)))
}

const patchCfoHomeWarmEntryState = (
  entry: CfoHomeWarmRegistryEntry,
  patch: (state: CfoHomeWarmState) => CfoHomeWarmState,
) => {
  setCfoHomeWarmEntryState(entry, patch(cloneWarmState(entry.state)))
}

const CFO_HOME_FG_TABLE_ALIASES: Record<string, string> = {
  account: 'fg_qbo_fpa_account',
  trial_balance_monthly: 'fg_qbo_fpa_trial_balance_monthly',
  gl_detail: QUICKBOOKS_GL_CREATOR_TABLE,
  cfo_statement_monthly_fact: 'fg_qbo_fpa_cfo_statement_monthly_fact',
  ar_support_detail: 'fg_qbo_fpa_ar_support_detail',
  ar_collectability_detail: 'fg_qbo_fpa_ar_collectability_detail',
  growth_invoice_metrics_summary: 'fg_qbo_fpa_growth_invoice_metrics_summary',
  ar_analysis: 'fg_qbo_fpa_ar_analysis_table',
  cfo_valuation_history: 'fg_qbo_fpa_cfo_valuation_history',
  layout_node: 'fg_qbo_fpa_layout_node',
  adjustment_line: 'fg_qbo_fpa_adjustment_line',
}

const quoteDuckIdent = (raw: string): string => `"${String(raw || '').replace(/"/g, '""')}"`
const quoteDuckString = (raw: string): string => `'${String(raw || '').replace(/'/g, "''")}'`

const withWarmTimeout = async <T,>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error(`${label} timed out after ${timeoutMs}ms`)), timeoutMs)
      }),
    ])
  } finally {
    if (timeoutId) clearTimeout(timeoutId)
  }
}

const tablePartBytes = (table: QuickBooksFpaCacheTablePayload): number[] => {
  const values = Array.isArray(table.parquet_part_bytes)
    ? table.parquet_part_bytes
    : Array.isArray(table.artifact_part_bytes)
      ? table.artifact_part_bytes
      : []
  return values.map((value) => Math.max(0, Math.floor(Number(value) || 0))).filter((value) => value > 0)
}

const tableBytes = (table: QuickBooksFpaCacheTablePayload): number =>
  tablePartBytes(table).reduce((sum, value) => sum + value, 0)

const cfoHomeWarmArtifactId = (table: QuickBooksFpaCacheTablePayload): string =>
  trimStr(table.id) || cfoHomeWarmCanonicalTableName(table)

const cfoHomeWarmArtifactLabel = (id: string): string =>
  CFO_HOME_ARTIFACT_LABELS[id] || id.replace(/^qbo_fpa_/, '').replace(/_/g, ' ')

const cfoHomeWarmArtifactPhase = (table: QuickBooksFpaCacheTablePayload): CfoHomeWarmArtifactPhase =>
  CFO_HOME_VISIBLE_ARTIFACT_IDS.has(cfoHomeWarmArtifactId(table)) ? 'home' : 'ai'

const cfoHomeWarmTableWeight = (table: QuickBooksFpaCacheTablePayload): number => {
  const bytes = tableBytes(table)
  if (bytes > 0) return bytes
  return Array.isArray(table.rows) ? table.rows.length : 0
}

const tableParquetUrls = (table: QuickBooksFpaCacheTablePayload): string[] => {
  if (Array.isArray(table.parquet_urls) && table.parquet_urls.length) {
    return table.parquet_urls.map((value) => trimStr(value)).filter(Boolean)
  }
  const single = trimStr(table.parquet_url)
  return single ? [single] : []
}

const cfoHomeWarmParquetUrls = (
  appApi: ServerApiClient,
  connectionId: string,
  table: QuickBooksFpaCacheTablePayload,
): string[] => {
  const rawUrls = tableParquetUrls(table)
  const fileId = trimStr(table.id)
  if (!rawUrls.length || !fileId || typeof appApi.getQuickBooksFpaCacheParquetUrl !== 'function') {
    return rawUrls
  }
  if (rawUrls.length > 1) {
    return rawUrls.map((_, index) => appApi.getQuickBooksFpaCacheParquetUrl(connectionId, fileId, index))
  }
  const raw = rawUrls[0] || ''
  const partMatch = raw.match(/[?&]part=(\d+)/)
  if (partMatch) {
    const part = Number(partMatch[1])
    return [appApi.getQuickBooksFpaCacheParquetUrl(connectionId, fileId, Number.isFinite(part) ? part : 0)]
  }
  return [appApi.getQuickBooksFpaCacheParquetUrl(connectionId, fileId)]
}

const cfoHomeWarmCanonicalTableName = (table: QuickBooksFpaCacheTablePayload): string =>
  trimStr(table.name) || `qbo_fpa_${trimStr(table.id).replace(/[^A-Za-z0-9_]/g, '_')}`

const cfoHomeWarmMaterializedTableName = (table: QuickBooksFpaCacheTablePayload): string =>
  tableParquetUrls(table).length
    ? cfoHomeWarmCanonicalTableName(table)
    : CFO_HOME_FG_TABLE_ALIASES[trimStr(table.id)] || `fg_${cfoHomeWarmCanonicalTableName(table).replace(/[^A-Za-z0-9_]/g, '_')}`

const cfoHomeWarmSort = (
  left: QuickBooksFpaCacheTablePayload,
  right: QuickBooksFpaCacheTablePayload,
): number => {
  const leftPhase = cfoHomeWarmArtifactPhase(left)
  const rightPhase = cfoHomeWarmArtifactPhase(right)
  if (leftPhase === 'ai' || rightPhase === 'ai') {
    const leftPriority = CFO_HOME_AI_WARM_PRIORITY[cfoHomeWarmArtifactId(left)] ?? 40
    const rightPriority = CFO_HOME_AI_WARM_PRIORITY[cfoHomeWarmArtifactId(right)] ?? 40
    if (leftPriority !== rightPriority) return leftPriority - rightPriority
  }
  const leftHasParquet = tableParquetUrls(left).length > 0
  const rightHasParquet = tableParquetUrls(right).length > 0
  if (leftHasParquet !== rightHasParquet) return leftHasParquet ? -1 : 1
  const byWeight = cfoHomeWarmTableWeight(right) - cfoHomeWarmTableWeight(left)
  if (byWeight !== 0) return byWeight
  return cfoHomeWarmCanonicalTableName(left).localeCompare(cfoHomeWarmCanonicalTableName(right))
}

const cfoHomeWarmRowColumns = (rows: any[]): string[] => {
  const seen = new Set<string>()
  for (const row of rows) {
    if (!row || typeof row !== 'object' || Array.isArray(row)) continue
    for (const key of Object.keys(row)) {
      const clean = trimStr(key)
      if (clean) seen.add(clean)
    }
  }
  return Array.from(seen)
}

const cfoHomeWarmCreateAlias = async (
  workerClient: AppWorkerClient,
  sourceTable: string,
  aliasTable: string,
  caller: string,
  cfoDuckDbOpts: Record<string, any>,
) => {
  const source = trimStr(sourceTable)
  const alias = trimStr(aliasTable)
  if (!source || !alias || source === alias) return
  await workerClient.duckdb_run_sql(
    `CREATE OR REPLACE VIEW ${quoteDuckIdent(alias)} AS SELECT * FROM ${quoteDuckIdent(source)}`,
    {
      ...cfoDuckDbOpts,
      caller,
      allowMutating: true,
      timeoutMs: 60000,
    },
  )
}

const cfoHomeWarmDuckColumns = async (
  workerClient: AppWorkerClient,
  tableName: string,
  cfoDuckDbOpts: Record<string, any>,
): Promise<Map<string, string>> => {
  const result = await workerClient.duckdb_run_sql(
    `
SELECT column_name AS name
FROM information_schema.columns
WHERE lower(table_name) = lower(${quoteDuckString(tableName)})
ORDER BY ordinal_position`,
    {
      ...cfoDuckDbOpts,
      caller: `QuickBooksCfoCore:ai_semantic:columns:${tableName}`,
      previewRowLimit: 400,
      timeoutMs: 30000,
    },
  )
  const headers = Array.isArray(result?.headers) ? result.headers.map((header: any) => String(header || '')) : []
  const rows = Array.isArray(result?.preview_rows) ? result.preview_rows : []
  const out = new Map<string, string>()
  for (const row of rows) {
    const raw = Array.isArray(row) ? row[headers.indexOf('name')] : row?.name
    const name = trimStr(raw)
    if (name) out.set(name.toLowerCase(), name)
  }
  return out
}

const cfoHomePickColumn = (columns: Map<string, string>, candidates: string[]): string | null => {
  for (const candidate of candidates) {
    const match = columns.get(candidate.toLowerCase())
    if (match) return match
  }
  return null
}

const cfoHomeColumnRef = (
  alias: string,
  columns: Map<string, string>,
  candidates: string[],
  fallback = 'NULL',
): string => {
  const column = cfoHomePickColumn(columns, candidates)
  return column ? `${alias}.${quoteDuckIdent(column)}` : fallback
}

const cfoHomeRequireColumn = (
  tableName: string,
  columns: Map<string, string>,
  candidates: string[],
  semanticName: string,
): string => {
  const column = cfoHomePickColumn(columns, candidates)
  if (!column) {
    throw new Error(
      `CFO AI semantic views cannot be built: ${tableName} is missing required semantic column ${semanticName}.`,
    )
  }
  return column
}

const cfoHomeCoalesceText = (expressions: string[], fallback: string): string =>
  `coalesce(${expressions.map((expr) => `nullif(trim(CAST(${expr} AS VARCHAR)), '')`).join(', ')}, ${fallback})`

const cfoHomeNormalBalanceSql = (classificationExpr: string, typeExpr: string): string => `
CASE
  WHEN lower(coalesce(${classificationExpr}, '')) IN ('revenue', 'income', 'liability', 'equity')
    OR lower(coalesce(${typeExpr}, '')) LIKE '%income%'
    OR lower(coalesce(${typeExpr}, '')) LIKE '%revenue%'
    OR lower(coalesce(${typeExpr}, '')) LIKE '%liability%'
    OR lower(coalesce(${typeExpr}, '')) LIKE '%equity%'
    OR lower(coalesce(${typeExpr}, '')) LIKE '%credit card%'
    OR lower(coalesce(${typeExpr}, '')) LIKE '%creditcard%'
  THEN 'credit'
  ELSE 'debit'
END`

const cfoHomeRunAiSemanticSql = async (
  workerClient: AppWorkerClient,
  cfoDuckDbOpts: Record<string, any>,
  sql: string,
  caller: string,
) => {
  await workerClient.duckdb_run_sql(sql, {
    ...cfoDuckDbOpts,
    caller,
    allowMutating: true,
    timeoutMs: 120000,
  })
}

const cfoHomeBuildAiSummaryViews = async (
  workerClient: AppWorkerClient,
  cfoDuckDbOpts: Record<string, any>,
  log: (event: string, payload?: Record<string, any>) => void,
): Promise<void> => {
  const started = performance.now()
  const accountTable = 'fg_qbo_fpa_account'
  const factTable = 'qbo_fpa_cfo_statement_monthly_fact'
  const [accountColumns, factColumns] = await Promise.all([
    cfoHomeWarmDuckColumns(workerClient, accountTable, cfoDuckDbOpts),
    cfoHomeWarmDuckColumns(workerClient, factTable, cfoDuckDbOpts),
  ])
  if (!accountColumns.size || !factColumns.size) {
    throw new Error(
      `CFO AI semantic views cannot be built: required source tables are missing (${accountTable}: ${accountColumns.size} columns, ${factTable}: ${factColumns.size} columns).`,
    )
  }
  const accountId = cfoHomeRequireColumn(accountTable, accountColumns, ['account_id', 'AccountId', 'AccountID', 'Id', 'id'], 'account_id')
  const factAccountId = cfoHomeRequireColumn(factTable, factColumns, ['account_id', 'AccountId', 'AccountID'], 'account_id')
  const factPeriod = cfoHomeRequireColumn(factTable, factColumns, ['period_start', 'PeriodStart', 'month', 'Month'], 'period_start')
  const factActivity = cfoHomeRequireColumn(
    factTable,
    factColumns,
    ['monthly_activity', 'MonthlyActivity', 'signed_activity', 'SignedActivity', 'MonthlyActivitySigned'],
    'monthly_activity',
  )
  const aId = `a.${quoteDuckIdent(accountId)}`
  const pId = `p.${quoteDuckIdent(accountId)}`
  const aParent = cfoHomeColumnRef('a', accountColumns, ['parent_account_id', 'ParentRef_value', 'ParentAccountId', 'parent_id'])
  const aName = cfoHomeColumnRef('a', accountColumns, ['name', 'Name'], "''")
  const pName = cfoHomeColumnRef('p', accountColumns, ['name', 'Name'], "''")
  const aFqn = cfoHomeColumnRef('a', accountColumns, ['fully_qualified_name', 'FullyQualifiedName', 'account_breadcrumb', 'AccountBreadcrumb'], "''")
  const aNumber = cfoHomeColumnRef('a', accountColumns, ['account_number', 'AcctNum', 'AccountNumber'], "''")
  const aType = cfoHomeColumnRef('a', accountColumns, ['account_type', 'AccountType', 'qbo_account_type'], "''")
  const aSubType = cfoHomeColumnRef('a', accountColumns, ['account_sub_type', 'AccountSubType'], "''")
  const aClass = cfoHomeColumnRef('a', accountColumns, ['classification', 'Classification', 'account_class', 'AccountClass'], "''")
  const parentJoin = aParent === 'NULL' ? 'false' : `${pId} = ${aParent}`
  const accountLabel = cfoHomeCoalesceText([aFqn, aName, aId], "''")
  const categoryLabel = `
CASE
  WHEN trim(coalesce(${pName}, '')) <> '' THEN trim(coalesce(${pName}, ''))
  WHEN trim(coalesce(${aFqn}, '')) <> '' AND strpos(${aFqn}, ':') > 0 THEN trim(split_part(${aFqn}, ':', 1))
  WHEN trim(coalesce(${aSubType}, '')) <> '' THEN trim(coalesce(${aSubType}, ''))
  WHEN trim(coalesce(${aType}, '')) <> '' THEN trim(coalesce(${aType}, ''))
  ELSE 'Uncategorized'
END`
  await cfoHomeRunAiSemanticSql(
    workerClient,
    cfoDuckDbOpts,
    `
CREATE OR REPLACE VIEW fg_cfo_ai_account AS
SELECT
  ${aId} AS account_id,
  ${accountLabel} AS account_label,
  nullif(trim(CAST(${aName} AS VARCHAR)), '') AS account_name,
  nullif(trim(CAST(${aNumber} AS VARCHAR)), '') AS account_number,
  ${cfoHomeCoalesceText([aFqn, aName, aId], "''")} AS account_breadcrumb,
  nullif(trim(CAST(${aClass} AS VARCHAR)), '') AS account_class,
  nullif(trim(CAST(${aType} AS VARCHAR)), '') AS account_type,
  nullif(trim(CAST(${aSubType} AS VARCHAR)), '') AS account_sub_type,
  ${categoryLabel} AS category_label,
  ${cfoHomeNormalBalanceSql(aClass, aType)} AS normal_balance
FROM ${quoteDuckIdent(accountTable)} a
LEFT JOIN ${quoteDuckIdent(accountTable)} p ON ${parentJoin}
WHERE nullif(trim(CAST(${aId} AS VARCHAR)), '') IS NOT NULL`,
    'QuickBooksCfoCore:ai_semantic:create_account_view',
  )

  const fAccountId = `f.${quoteDuckIdent(factAccountId)}`
  const fPeriod = `f.${quoteDuckIdent(factPeriod)}`
  const fActivity = `f.${quoteDuckIdent(factActivity)}`
  const fPeriodEnd = cfoHomeColumnRef('f', factColumns, ['period_end', 'PeriodEnd'])
  const fStatementType = cfoHomeColumnRef('f', factColumns, ['statement_type', 'StatementType'], "''")
  const fSource = cfoHomeColumnRef('f', factColumns, ['source', 'Source'], "''")
  const fWorkbookId = cfoHomeColumnRef('f', factColumns, ['workbook_id'], "''")
  const fConnectionId = cfoHomeColumnRef('f', factColumns, ['connection_id'], "''")
  const fLayoutVersionId = cfoHomeColumnRef('f', factColumns, ['layout_version_id', 'version_id', 'VersionId'], "''")
  const fLayoutNodeId = cfoHomeColumnRef('f', factColumns, ['layout_node_id', 'node_id', 'NodeId'], "''")
  const fAmount = cfoHomeColumnRef('f', factColumns, ['amount', 'rendered_amount', 'Amount'], fActivity)
  const fAccountLabel = cfoHomeColumnRef('f', factColumns, ['account_label', 'account', 'Account', 'line_label'], "''")
  const fAccountNumber = cfoHomeColumnRef('f', factColumns, ['account_number', 'AccountNumber'], "''")
  const fBreadcrumb = cfoHomeColumnRef('f', factColumns, ['account_breadcrumb', 'AccountBreadcrumb'], "''")
  const fAccountClass = cfoHomeColumnRef('f', factColumns, ['account_class', 'classification', 'AccountClass'], "''")
  const fAccountType = cfoHomeColumnRef('f', factColumns, ['account_type', 'AccountType'], "''")
  const fCategory = cfoHomeColumnRef('f', factColumns, ['category_label', 'parent_category_label'], "''")
  const fNormalBalance = cfoHomeColumnRef('f', factColumns, ['normal_balance', 'NormalBalance'], "''")
  await cfoHomeRunAiSemanticSql(
    workerClient,
    cfoDuckDbOpts,
    `
CREATE OR REPLACE VIEW fg_cfo_ai_statement_monthly_activity AS
SELECT
  nullif(trim(CAST(${fConnectionId} AS VARCHAR)), '') AS connection_id,
  nullif(trim(CAST(${fWorkbookId} AS VARCHAR)), '') AS workbook_id,
  nullif(trim(CAST(${fLayoutVersionId} AS VARCHAR)), '') AS layout_version_id,
  CAST(date_trunc('month', TRY_CAST(${fPeriod} AS DATE)) AS VARCHAR) AS period_start,
  coalesce(
    nullif(trim(CAST(${fPeriodEnd} AS VARCHAR)), ''),
    CAST(TRY_CAST(date_trunc('month', TRY_CAST(${fPeriod} AS DATE)) AS DATE) + INTERVAL 1 MONTH - INTERVAL 1 DAY AS VARCHAR)
  ) AS period_end,
  CASE
    WHEN trim(coalesce(${fStatementType}, '')) <> '' THEN trim(CAST(${fStatementType} AS VARCHAR))
    WHEN lower(coalesce(a.account_class, '')) IN ('revenue', 'income', 'expense') OR lower(coalesce(a.account_type, '')) LIKE '%expense%' OR lower(coalesce(a.account_type, '')) LIKE '%income%' THEN 'profit_and_loss'
    WHEN lower(coalesce(a.account_class, '')) IN ('asset', 'liability', 'equity') OR lower(coalesce(a.account_type, '')) LIKE '%asset%' OR lower(coalesce(a.account_type, '')) LIKE '%liability%' OR lower(coalesce(a.account_type, '')) LIKE '%equity%' THEN 'balance_sheet'
    ELSE 'unknown'
  END AS statement_type,
  ${fAccountId} AS account_id,
  ${cfoHomeCoalesceText([fAccountLabel, 'a.account_label', fAccountId], "''")} AS account_label,
  coalesce(nullif(trim(CAST(${fAccountNumber} AS VARCHAR)), ''), a.account_number) AS account_number,
  ${cfoHomeCoalesceText([fBreadcrumb, 'a.account_breadcrumb', fAccountLabel, fAccountId], "''")} AS account_breadcrumb,
  coalesce(nullif(trim(CAST(${fAccountClass} AS VARCHAR)), ''), a.account_class) AS account_class,
  coalesce(nullif(trim(CAST(${fAccountType} AS VARCHAR)), ''), a.account_type) AS account_type,
  a.account_sub_type,
  coalesce(nullif(trim(CAST(${fCategory} AS VARCHAR)), ''), a.category_label, 'Uncategorized') AS category_label,
  nullif(trim(CAST(${fLayoutNodeId} AS VARCHAR)), '') AS layout_node_id,
  coalesce(nullif(trim(CAST(${fNormalBalance} AS VARCHAR)), ''), a.normal_balance) AS normal_balance,
  coalesce(TRY_CAST(${fActivity} AS DOUBLE), 0) AS monthly_activity,
  coalesce(TRY_CAST(${fAmount} AS DOUBLE), coalesce(TRY_CAST(${fActivity} AS DOUBLE), 0)) AS amount,
  coalesce(nullif(trim(CAST(${fSource} AS VARCHAR)), ''), 'cfo_statement_monthly_fact') AS source,
  lower(coalesce(CAST(${fSource} AS VARCHAR), '')) = 'fusegridadjustment' AS is_adjustment,
  CASE
    WHEN lower(coalesce(CAST(${fSource} AS VARCHAR), '')) = 'fusegridadjustment' THEN 'qbo_fpa_cfo_statement_monthly_fact,fg_qbo_fpa_adjustment_line'
    ELSE 'qbo_fpa_cfo_statement_monthly_fact,fg_qbo_fpa_trial_balance_monthly'
  END AS source_table_names
FROM ${quoteDuckIdent(factTable)} f
LEFT JOIN fg_cfo_ai_account a ON a.account_id = ${fAccountId}
WHERE nullif(trim(CAST(${fAccountId} AS VARCHAR)), '') IS NOT NULL
  AND TRY_CAST(${fPeriod} AS DATE) IS NOT NULL`,
    'QuickBooksCfoCore:ai_semantic:create_statement_view',
  )

  await cfoHomeRunAiSemanticSql(
    workerClient,
    cfoDuckDbOpts,
    `
CREATE OR REPLACE VIEW fg_cfo_ai_opex_monthly_activity AS
SELECT *
FROM fg_cfo_ai_statement_monthly_activity
WHERE (
    lower(coalesce(account_class, '')) = 'expense'
    OR lower(coalesce(account_type, '')) LIKE '%expense%'
    OR lower(coalesce(account_type, '')) = 'other expense'
  )
  AND lower(coalesce(account_type, '')) NOT LIKE '%cost of goods%'
  AND lower(coalesce(account_type, '')) NOT LIKE '%cogs%'
  AND lower(coalesce(account_breadcrumb, '')) NOT LIKE '%cost of goods%'
  AND lower(coalesce(account_breadcrumb, '')) NOT LIKE '%cogs%'`,
    'QuickBooksCfoCore:ai_semantic:create_opex_view',
  )

  // Modeled-value history (one row per month) — a tabular companion to the
  // history embedded in the valuation overview, so the agent can SQL it. Built
  // best-effort: skipped (not fatal) when the artifact isn't materialized.
  const valuationHistoryTable = 'qbo_fpa_cfo_valuation_history'
  const valuationHistoryColumns = await cfoHomeWarmDuckColumns(
    workerClient,
    valuationHistoryTable,
    cfoDuckDbOpts,
  )
  if (valuationHistoryColumns.size) {
    await cfoHomeRunAiSemanticSql(
      workerClient,
      cfoDuckDbOpts,
      `
CREATE OR REPLACE VIEW fg_cfo_ai_valuation_history AS
SELECT
  nullif(trim(CAST(connection_id AS VARCHAR)), '') AS connection_id,
  nullif(trim(CAST(workbook_id AS VARCHAR)), '') AS workbook_id,
  CAST(period AS VARCHAR) AS period,
  TRY_CAST(revenue_ttm AS DOUBLE) AS revenue_ttm,
  TRY_CAST(ebitda_ttm AS DOUBLE) AS ebitda_ttm,
  TRY_CAST(net_income_ttm AS DOUBLE) AS net_income_ttm,
  TRY_CAST(revenue_growth AS DOUBLE) AS revenue_growth,
  TRY_CAST(ebitda_margin AS DOUBLE) AS ebitda_margin,
  TRY_CAST(net_margin AS DOUBLE) AS net_margin,
  TRY_CAST(total_debt AS DOUBLE) AS total_debt,
  TRY_CAST(cash AS DOUBLE) AS cash,
  TRY_CAST(equity_value_low AS DOUBLE) AS equity_value_low,
  TRY_CAST(equity_value_mid AS DOUBLE) AS equity_value_mid,
  TRY_CAST(equity_value_high AS DOUBLE) AS equity_value_high,
  TRY_CAST(earnings_equity AS DOUBLE) AS earnings_equity,
  TRY_CAST(ebitda_equity AS DOUBLE) AS ebitda_equity,
  TRY_CAST(revenue_equity AS DOUBLE) AS revenue_equity,
  TRY_CAST(dcf_equity AS DOUBLE) AS dcf_equity
FROM ${quoteDuckIdent(valuationHistoryTable)}
WHERE nullif(trim(CAST(period AS VARCHAR)), '') IS NOT NULL
ORDER BY period`,
      'QuickBooksCfoCore:ai_semantic:create_valuation_history_view',
    )
  }
  log('ai_semantic_views_ready', {
    account_table: accountTable,
    fact_table: factTable,
    elapsed_ms: Math.round(performance.now() - started),
  })
}

const cfoHomeBuildAiGlView = async (
  workerClient: AppWorkerClient,
  cfoDuckDbOpts: Record<string, any>,
  log: (event: string, payload?: Record<string, any>) => void,
): Promise<void> => {
  const started = performance.now()
  const glTable = QUICKBOOKS_GL_CREATOR_TABLE
  const glColumns = await cfoHomeWarmDuckColumns(workerClient, glTable, cfoDuckDbOpts)
  if (!glColumns.size) {
    throw new Error(`CFO AI GL view cannot be built: ${glTable} is not materialized yet.`)
  }
  const accountId = cfoHomeRequireColumn(glTable, glColumns, ['account_id', 'AccountId', 'AccountID'], 'account_id')
  const txnDate = cfoHomeRequireColumn(glTable, glColumns, ['txn_date', 'date', 'Date', 'TxnDate'], 'txn_date')
  const account = cfoHomeColumnRef('gl', glColumns, ['account_label', 'account_name', 'account', 'Account'], "''")
  const amount = cfoHomeColumnRef('gl', glColumns, ['amount', 'Amount'])
  const debit = cfoHomeColumnRef('gl', glColumns, ['debit', 'Debit'], '0')
  const credit = cfoHomeColumnRef('gl', glColumns, ['credit', 'Credit'], '0')
  const amountExpr = amount === 'NULL'
    ? `coalesce(TRY_CAST(${debit} AS DOUBLE), 0) - coalesce(TRY_CAST(${credit} AS DOUBLE), 0)`
    : `
CASE
  WHEN abs(coalesce(TRY_CAST(${debit} AS DOUBLE), 0)) + abs(coalesce(TRY_CAST(${credit} AS DOUBLE), 0)) > 0
  THEN coalesce(TRY_CAST(${debit} AS DOUBLE), 0) - coalesce(TRY_CAST(${credit} AS DOUBLE), 0)
  ELSE coalesce(TRY_CAST(${amount} AS DOUBLE), 0)
END`
  await cfoHomeRunAiSemanticSql(
    workerClient,
    cfoDuckDbOpts,
    `
CREATE OR REPLACE VIEW fg_cfo_ai_gl_detail AS
SELECT
  CAST(TRY_CAST(gl.${quoteDuckIdent(txnDate)} AS DATE) AS VARCHAR) AS txn_date,
  gl.${quoteDuckIdent(accountId)} AS account_id,
  ${cfoHomeCoalesceText([account, 'a.account_label', `gl.${quoteDuckIdent(accountId)}`], "''")} AS account_label,
  nullif(trim(CAST(${cfoHomeColumnRef('gl', glColumns, ['vendor', 'Vendor'], "''")} AS VARCHAR)), '') AS vendor,
  nullif(trim(CAST(${cfoHomeColumnRef('gl', glColumns, ['customer', 'Customer'], "''")} AS VARCHAR)), '') AS customer,
  nullif(trim(CAST(${cfoHomeColumnRef('gl', glColumns, ['name', 'Name'], "''")} AS VARCHAR)), '') AS name,
  trim(CAST(${cfoHomeColumnRef('gl', glColumns, ['memo', 'Memo'], "''")} AS VARCHAR)) AS memo,
  trim(CAST(${cfoHomeColumnRef('gl', glColumns, ['transaction_type', 'TransactionType'], "''")} AS VARCHAR)) AS transaction_type,
  trim(CAST(${cfoHomeColumnRef('gl', glColumns, ['num', 'Num', 'document_number'], "''")} AS VARCHAR)) AS document_number,
  ${amountExpr} AS amount,
  'fg_qbo_fpa_gl_detail' AS source_table_names
FROM ${quoteDuckIdent(glTable)} gl
LEFT JOIN fg_cfo_ai_account a ON a.account_id = gl.${quoteDuckIdent(accountId)}
WHERE nullif(trim(CAST(gl.${quoteDuckIdent(accountId)} AS VARCHAR)), '') IS NOT NULL
  AND TRY_CAST(gl.${quoteDuckIdent(txnDate)} AS DATE) IS NOT NULL`,
    'QuickBooksCfoCore:ai_semantic:create_gl_view',
  )
  log('ai_gl_view_ready', {
    gl_table: glTable,
    elapsed_ms: Math.round(performance.now() - started),
  })
}

async function warmCfoHomeAiArtifacts(
  appApi: ServerApiClient,
  workerClient: AppWorkerClient,
  workbookId: string,
  log: (event: string, payload?: Record<string, any>) => void,
  onProgress?: (state: CfoHomeWarmState) => void,
  includeAiArtifacts = CFO_HOME_WARM_AI_ARTIFACTS_ON_LOAD,
): Promise<void> {
  const started = performance.now()
  const publish = (state: CfoHomeWarmState) => {
    onProgress?.(state)
  }
  const loadBootstrap = (appApi.getCfoCoreBootstrap || appApi.getQuickBooksCfoCoreBootstrap)?.bind(appApi)
  if (typeof loadBootstrap !== 'function') throw new Error('CFO Core bootstrap contract unavailable.')
  const bootstrap = await loadBootstrap(workbookId)
  if (bootstrap.accounting_provider && bootstrap.accounting_provider !== 'quickbooks') {
    log('warm_skipped', { workbook_id: workbookId, reason: 'server_governed_provider' })
    return
  }
  const connectionId = trimStr(
    (bootstrap as any)?.connection?.connection_id
      || (bootstrap as any)?.connection_id
      || (bootstrap as any)?.bindings?.[0]?.connection_id,
  )
  if (!connectionId) throw new Error('CFO Home warm cache could not resolve a QuickBooks connection.')
  const executionScopeId = buildTabExecutionScopeId({
    browserTabId: getBrowserTabId(),
    workbookScopeId: workbookId,
  })
  const cfoDuckDbOpts = {
    executionScopeId,
    persistentAdHoc: true,
    persistentDatasetId: CFO_CORE_DUCKDB_DATASET_ID,
  }

  const prepared = await appApi.prepareQuickBooksFpaCache(connectionId, {
    includeGl: includeAiArtifacts,
    asyncDetail: true,
    includeAdjustments: includeAiArtifacts,
    includeArSupport: includeAiArtifacts,
    includeArCollectability: includeAiArtifacts,
    includeArAnalysis: includeAiArtifacts,
    workbookId,
  })
  const tables = Array.isArray(prepared.tables) ? prepared.tables : []
  const allWarmable = tables
    .filter((table) => tableParquetUrls(table).length || (Array.isArray(table.rows) && table.rows.length))
  const homeWarmable = allWarmable
    .filter((table) => cfoHomeWarmArtifactPhase(table) === 'home')
    .sort(cfoHomeWarmSort)
  const aiWarmable = allWarmable
    .filter((table) => cfoHomeWarmArtifactPhase(table) !== 'home')
    .sort(cfoHomeWarmSort)
  const warmable = includeAiArtifacts ? [...homeWarmable, ...aiWarmable] : homeWarmable
  const expectedBytes = warmable.reduce((sum, table) => sum + tableBytes(table), 0)
  const expectedRows = warmable.reduce((sum, table) => sum + Number(table.row_count_hint || table.rows?.length || 0), 0)
  const artifactProgress = [
    ...warmable.map((table): CfoHomeWarmArtifactProgress => {
      const id = cfoHomeWarmArtifactId(table)
      return {
        id,
        label: cfoHomeWarmArtifactLabel(id),
        phase: cfoHomeWarmArtifactPhase(table),
        status: 'pending',
        rows: Number(table.row_count_hint || table.rows?.length || 0),
        bytes: tableBytes(table),
      }
    }),
    ...(includeAiArtifacts
      ? [
          {
            id: 'ai_semantic_views',
            label: cfoHomeWarmArtifactLabel('ai_semantic_views'),
            phase: 'ai' as const,
            status: 'pending' as const,
            rows: 0,
            bytes: 0,
          },
          {
            id: 'ai_gl_view',
            label: cfoHomeWarmArtifactLabel('ai_gl_view'),
            phase: 'ai' as const,
            status: 'pending' as const,
            rows: 0,
            bytes: 0,
          },
        ]
      : []),
  ]
  let currentState: CfoHomeWarmState = {
    status: 'warming',
    artifacts: artifactProgress,
    home_ready_ms: null,
    all_ready_ms: null,
    current_artifact: null,
    error: null,
  }
  const updateArtifact = (
    id: string,
    patch: Partial<CfoHomeWarmArtifactProgress>,
    statePatch: Partial<CfoHomeWarmState> = {},
  ) => {
    currentState = {
      ...currentState,
      ...statePatch,
      artifacts: currentState.artifacts.map((artifact) =>
        artifact.id === id ? { ...artifact, ...patch } : artifact,
      ),
    }
    publish(currentState)
  }
  publish(currentState)
  log('warm_start', {
    workbook_id: workbookId,
    connection_id: connectionId,
    execution_scope_id: executionScopeId,
    tables: warmable.length,
    home_tables: homeWarmable.length,
    ai_tables: aiWarmable.length,
    expected_rows: expectedRows,
    expected_bytes: expectedBytes,
    cfo_analytics_ready: prepared.manifest?.cfo_analytics_ready ?? null,
    ml_forecast_ready: prepared.manifest?.ml_forecast_ready ?? null,
    ml_signals_ready: prepared.manifest?.ml_signals_ready ?? null,
  })

  const materialized: Array<{ id: string; table: string; rows: number; bytes: number; elapsed_ms: number }> = []
  const materializedIds = new Set<string>()
  let aiSummaryViewsReady = false
  let aiGlViewReady = false
  for (const table of warmable) {
    const tableStarted = performance.now()
    const artifactId = cfoHomeWarmArtifactId(table)
    const canonicalName = cfoHomeWarmCanonicalTableName(table)
    const materializedName = cfoHomeWarmMaterializedTableName(table)
    const urls = cfoHomeWarmParquetUrls(appApi, connectionId, table)
    const phase = cfoHomeWarmArtifactPhase(table)
    const caller = `QuickBooksCfoCore:${phase === 'home' ? 'home_ready_warm' : 'ai_deep_warm'}:${artifactId}`
    updateArtifact(artifactId, { status: 'downloading' }, { current_artifact: artifactId })
    log('warm_table_start', {
      id: artifactId,
      phase,
      table: materializedName,
      canonical_table: canonicalName,
      parquet_parts: urls.length,
      inline_rows: Array.isArray(table.rows) ? table.rows.length : 0,
      bytes: tableBytes(table),
      rows: Number(table.row_count_hint || table.rows?.length || 0),
    })
    if (urls.length) {
      const parquetTimeoutMs = phase === 'home' ? 45000 : 180000
      await withWarmTimeout(
        workerClient.duckdb_create_table_from_parquet_urls(materializedName, urls, {
          ...cfoDuckDbOpts,
          persistent: true,
          caller,
          sharedPartCacheKey: [
            trimStr(table.id) || canonicalName,
            trimStr(table.extract_version_id),
            Number(table.row_count_hint || table.rows?.length || 0) || 0,
            tableBytes(table),
            ...tablePartBytes(table),
          ].join('|'),
          partBytes: tablePartBytes(table),
          timeoutMs: 900000,
        }),
        parquetTimeoutMs,
        `CFO Home warm parquet table ${trimStr(table.id) || canonicalName}`,
      )
    } else {
      const rows = Array.isArray(table.rows) ? table.rows : []
      const columns = cfoHomeWarmRowColumns(rows)
      if (!columns.length) continue
      await withWarmTimeout(
        workerClient.duckdb_create_temp_table_from_rows(materializedName, columns, rows, {
          ...cfoDuckDbOpts,
          persistent: true,
          caller,
          dropIfExists: true,
          maxRows: 250000,
          chunkSize: 1000,
          timeoutMs: 180000,
        }),
        30000,
        `CFO Home warm inline table ${trimStr(table.id) || canonicalName}`,
      )
    }
    await cfoHomeWarmCreateAlias(workerClient, materializedName, canonicalName, `${caller}:canonical_alias`, cfoDuckDbOpts)
    const fgAlias = CFO_HOME_FG_TABLE_ALIASES[trimStr(table.id)]
    if (fgAlias) await cfoHomeWarmCreateAlias(workerClient, materializedName, fgAlias, `${caller}:fg_alias`, cfoDuckDbOpts)
    const materializedItem = {
      id: artifactId,
      table: materializedName,
      rows: Number(table.row_count_hint || table.rows?.length || 0),
      bytes: tableBytes(table),
      elapsed_ms: Math.round(performance.now() - tableStarted),
    }
    materialized.push(materializedItem)
    materializedIds.add(artifactId)
    updateArtifact(artifactId, { status: 'ready', elapsed_ms: materializedItem.elapsed_ms })
    log('warm_table_done', { ...materializedItem, phase })
    if (includeAiArtifacts && !aiSummaryViewsReady && materializedIds.has('account') && materializedIds.has('cfo_statement_monthly_fact')) {
      const semanticStarted = performance.now()
      updateArtifact('ai_semantic_views', { status: 'downloading' }, { current_artifact: 'ai_semantic_views' })
      log('ai_semantic_views_start', {
        workbook_id: workbookId,
        connection_id: connectionId,
        required_tables: ['account', 'cfo_statement_monthly_fact'],
      })
      try {
        await cfoHomeBuildAiSummaryViews(workerClient, cfoDuckDbOpts, log)
        aiSummaryViewsReady = true
        // Flip the agent's readiness cache the instant the views exist, so a
        // drilldown asked seconds after a click finds them without re-probing.
        markCfoArtifactReady(workerClient, cfoDuckDbOpts, 'fg_cfo_ai_statement_monthly_activity')
        markCfoArtifactReady(workerClient, cfoDuckDbOpts, 'fg_cfo_ai_opex_monthly_activity')
        updateArtifact('ai_semantic_views', {
          status: 'ready',
          elapsed_ms: Math.round(performance.now() - semanticStarted),
        })
      } catch (error: any) {
        const message = trimStr(error?.message || error) || 'CFO AI semantic view build failed.'
        updateArtifact('ai_semantic_views', { status: 'failed' }, { error: message })
        log('ai_semantic_views_failed', { workbook_id: workbookId, connection_id: connectionId, error: message })
        throw error
      }
    }
    if (includeAiArtifacts && !aiGlViewReady && aiSummaryViewsReady && materializedIds.has('gl_detail')) {
      const glStarted = performance.now()
      updateArtifact('ai_gl_view', { status: 'downloading' }, { current_artifact: 'ai_gl_view' })
      log('ai_gl_view_start', {
        workbook_id: workbookId,
        connection_id: connectionId,
        required_tables: ['gl_detail', 'ai_semantic_views'],
      })
      try {
        await cfoHomeBuildAiGlView(workerClient, cfoDuckDbOpts, log)
        aiGlViewReady = true
        markCfoArtifactReady(workerClient, cfoDuckDbOpts, 'fg_cfo_ai_gl_detail')
        updateArtifact('ai_gl_view', {
          status: 'ready',
          elapsed_ms: Math.round(performance.now() - glStarted),
        })
      } catch (error: any) {
        const message = trimStr(error?.message || error) || 'CFO AI GL view build failed.'
        updateArtifact('ai_gl_view', { status: 'failed' }, { error: message })
        log('ai_gl_view_failed', { workbook_id: workbookId, connection_id: connectionId, error: message })
      }
    }
    const homeReady =
      currentState.home_ready_ms == null
      && currentState.artifacts
        .filter((artifact) => artifact.phase === 'home')
        .every((artifact) => artifact.status === 'ready')
    if (homeReady) {
      const homeReadyMs = Math.round(performance.now() - started)
      currentState = {
        ...currentState,
        status: 'home_ready',
        home_ready_ms: homeReadyMs,
        current_artifact: aiWarmable.length ? currentState.current_artifact : null,
      }
      publish(currentState)
      log('warm_home_ready', {
        workbook_id: workbookId,
        connection_id: connectionId,
        tables: currentState.artifacts.filter((artifact) => artifact.phase === 'home').length,
        bytes: currentState.artifacts
          .filter((artifact) => artifact.phase === 'home')
          .reduce((sum, artifact) => sum + artifact.bytes, 0),
        rows: currentState.artifacts
          .filter((artifact) => artifact.phase === 'home')
          .reduce((sum, artifact) => sum + artifact.rows, 0),
        elapsed_ms: homeReadyMs,
      })
    }
  }
  if (includeAiArtifacts && !aiSummaryViewsReady) {
    const message = 'CFO AI semantic views could not be built because account or cfo_statement_monthly_fact artifacts were not warmed.'
    updateArtifact('ai_semantic_views', { status: 'failed' }, { error: message })
    throw new Error(message)
  }
  if (includeAiArtifacts && !aiGlViewReady) {
    updateArtifact('ai_gl_view', { status: 'failed' })
    log('ai_gl_view_not_ready', {
      workbook_id: workbookId,
      connection_id: connectionId,
      reason: materializedIds.has('gl_detail') ? 'build_failed' : 'gl_detail_not_warmed',
    })
  }

  const countSql = materialized
    .map((item) => `SELECT ${quoteDuckString(item.id)} AS id, COUNT(*) AS rows FROM ${quoteDuckIdent(item.table)}`)
    .join('\nUNION ALL\n')
  const counts = countSql
    ? await workerClient.duckdb_run_sql(countSql, {
        ...cfoDuckDbOpts,
        caller: 'QuickBooksCfoCore:home_warm:verify_counts',
        previewRowLimit: 100,
        timeoutMs: 120000,
      }).catch((error: any) => ({ error: error?.message || String(error) }))
    : null
  currentState = {
    ...currentState,
    status: 'ready',
    all_ready_ms: Math.round(performance.now() - started),
    current_artifact: null,
  }
  publish(currentState)
  log('warm_done', {
    workbook_id: workbookId,
    connection_id: connectionId,
    tables: materialized.length,
    bytes: materialized.reduce((sum, item) => sum + item.bytes, 0),
    rows: materialized.reduce((sum, item) => sum + item.rows, 0),
    elapsed_ms: Math.round(performance.now() - started),
    counts,
  })
}

// The cfo-home "Brief" is the novice-first front door for QuickBooks CFO
// workbooks (plan Section 3 "Option A"). It is a thin composition layer over
// primitives that already exist: the canonical period-comparison engine
// (`getQuickBooksMetricComparisons`) for the KPI cards, the deterministic
// ambient insight spine (`getQuickBooksInsights`) for the ranked cards, the
// shared CfoAnalysisProvider/CfoAnalysisModal for the AI workspace, and the
// first-party telemetry/feedback spine for AI feedback. It does NOT re-derive
// any financial logic — every number flows from the shared server contracts so
// the Brief stays semantically identical to CFO Core, the ratio library, and
// the presentation surface.

export type CfoHomeBriefProps = {
  appApi: ServerApiClient
  workerClient?: AppWorkerClient | null
  workbookId: string | null
  selectedEndPeriod?: string | null
  onSelectedEndPeriodChange?: (value: string) => void
  periodOptions?: CfoShellSelectOption[]
  comparisonKind?: MetricComparisonKind
  onComparisonKindChange?: (value: MetricComparisonKind) => void
  comparisonOptions?: CfoShellSelectOption[]
  apiSessionReady?: boolean
  /**
   * Deep-link out of the Brief into the existing CFO Core ribbon. The host
   * (layout) switches the active tab to `cfo-core` and hands the drill target
   * (tab/view/params) to the CFO Core surface.
   */
  onOpenCfoCore?: (target?: InsightDrillTarget | null) => void
  /**
   * Open/create the existing Client Financials presentation tab
   * (`cfo-presentation`). Used by the guided-journey "Wrap & share" handoff.
   */
  onOpenCfoPresentation?: () => void
  /**
   * In-shell nav handler (unified surface router). When provided, the rail
   * routes section selection to a native interior page inside the shell instead
   * of deep-linking out to the legacy `cfo-core` tab. Additive / optional:
   * without it, the Brief keeps its original deep-link behavior.
   */
  onNavSelect?: (navId: string) => void
  navProfile?: CfoNavProfile
  runtimeProfile?: CfoRuntimeProfile
}

const SURFACE = CFO_SURFACES.home

const KPI_DEFS: Array<{ id: string; label: string }> = [
  { id: 'revenue', label: 'Total revenue' },
  { id: 'net_income', label: 'Net income' },
  { id: 'ebitda', label: 'EBITDA' },
  { id: 'cash', label: 'Cash on hand' },
]

const HOME_COMPARISON_METRIC_IDS = Array.from(
  new Set([
    ...KPI_DEFS.map((def) => def.id),
    'cost_of_sales',
    'gross_profit',
    'operating_expenses',
  ]),
)

const cfoHomeInsightsFromContract = (home: CfoHomeResponse): CfoInsightsResponse => ({
  workbook_id: home.workbook_id,
  connection_id: home.connection_id,
  realm_id: home.realm_id,
  as_of: home.as_of,
  grain: 'month',
  insight_set_version: home.contract.contract_version,
  signature: `cfohome_${home.contract.freshness_token || home.contract.generated_at}`,
  attention_count: home.attention_count,
  cards: Array.isArray(home.alerts) ? home.alerts : [],
  warnings: Array.isArray(home.contract.warnings) ? home.contract.warnings : [],
})

const GUIDED_JOURNEYS: Array<{ id: string; icon: string; title: string; subtitle: string }> = [
  { id: 'monthly_review', icon: '\u25A4', title: 'Monthly Review', subtitle: 'Walk this month end-to-end' },
  { id: 'board_prep', icon: '\u25A6', title: 'Board Prep', subtitle: 'Build a shareable packet' },
  { id: 'cash_check', icon: '\u224B', title: 'Cash Check', subtitle: '13-week look ahead' },
  { id: 'improve_profitability', icon: '\u25D4', title: 'Improve Profitability', subtitle: 'Find the opex creep' },
  { id: 'plan_next_year', icon: '\u25C8', title: 'Plan Next Year', subtitle: 'Auto-draft a budget' },
  { id: 'valuation', icon: '$', title: 'Valuation', subtitle: "What's it worth?" },
]

const trimStr = (value: unknown): string => String(value ?? '').trim()

const formatRatio = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(value)) return '—'
  return `${(value * 100).toFixed(1)}%`
}

const formatSignedPct = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(value)) return '—'
  const sign = value > 0 ? '+' : value < 0 ? '\u2212' : ''
  return `${sign}${Math.abs(value * 100).toFixed(1)}%`
}

const formatImpact = (card: InsightCard): string => {
  const { value, unit } = card.impact
  if (!Number.isFinite(value)) return '—'
  const sign = value > 0 ? '+' : value < 0 ? '\u2212' : ''
  const abs = Math.abs(value)
  switch (unit) {
    case 'usd':
      return `${sign}${formatCurrencyCompact(abs)}`
    case 'pct':
      return `${sign}${abs.toFixed(1)}%`
    case 'pct_pts':
      return `${sign}${abs.toFixed(1)} pts`
    case 'months':
      return `~${abs.toFixed(0)} mo`
    case 'ratio':
      return `${sign}${abs.toFixed(2)}`
    default:
      return `${sign}${abs}`
  }
}

const SEVERITY_LABEL: Record<InsightCard['severity'], string> = {
  good: 'Looking good',
  check: 'Double-check',
  watch: 'Keep an eye',
  bad: 'Needs attention',
}

// Insight severity → instant-card verdict grade, so the card pill matches the
// rail card the user clicked instead of being re-derived from an unrelated delta.
const SEVERITY_GRADE: Record<InsightCard['severity'], 'good' | 'watch' | 'risk' | 'neutral'> = {
  good: 'good',
  check: 'watch',
  watch: 'watch',
  bad: 'risk',
}

// Detectors that are diagnostics about OTHER surfaces (e.g. forecast interval
// width) rather than something visible on the Home brief. Surfacing them here
// warns the user about a forecast they haven't seen yet — they stay on the
// surfaces that render the forecast itself.
const HOME_HIDDEN_INSIGHT_DETECTORS = new Set(['low_confidence_forecast'])

const filterHomeInsights = (resp: CfoInsightsResponse): CfoInsightsResponse => {
  if (!resp.cards.some((card) => HOME_HIDDEN_INSIGHT_DETECTORS.has(card.detector))) return resp
  const cards = resp.cards.filter((card) => !HOME_HIDDEN_INSIGHT_DETECTORS.has(card.detector))
  return {
    ...resp,
    cards,
    attention_count: cards.filter((card) => card.severity !== 'good').length,
  }
}

// Compact-glance helpers: a severity dot tone and a short chip label for the
// one-line digest's attention chips.
const severityDotClass = (sev: InsightCard['severity']): string =>
  sev === 'bad' ? 'bad' : sev === 'good' ? 'good' : 'watch'

const insightChipLabel = (card: InsightCard): string => {
  const metric = String(card.impact?.metric_id ?? '').replace(/_/g, ' ').trim()
  if (metric) return metric.charAt(0).toUpperCase() + metric.slice(1)
  const headline = String(card.headline ?? '').trim()
  return headline.length > 30 ? `${headline.slice(0, 29)}…` : headline
}

/** Pick the metric row for a KPI id, tolerant of registry id drift. */
const findMetricRow = (resp: MetricComparisonResponse | null, id: string): MetricComparisonRow | null => {
  if (!resp) return null
  const exact = resp.metrics.find((m) => m.id === id)
  if (exact) return exact
  const lowered = id.toLowerCase()
  return resp.metrics.find((m) => m.id.toLowerCase() === lowered || m.label.toLowerCase().includes(lowered)) || null
}

const pickComparison = (row: MetricComparisonRow | null, kind: MetricComparisonKind) =>
  row?.comparisons.find((c) => c.kind === kind) || null

const instantComparisonTone = (value: number | null | undefined): 'neutral' | 'good' | 'bad' => {
  if (value == null || !Number.isFinite(value) || Math.abs(value) < 0.000001) return 'neutral'
  return value > 0 ? 'good' : 'bad'
}

const formatMetricComparisonValue = (value: number | null | undefined, row: MetricComparisonRow | null): string => {
  if (!row) return '—'
  return row.value_kind === 'ratio' ? formatRatio(value) : formatCurrencyCompact(value)
}

// A % change off a near-zero prior base (e.g. -$270 → $32.3K = "+12,072.8%")
// reads as noise, not a comparison. Past ±400% show the absolute dollar move
// instead; ratios keep the % form since their base is already normalized.
const MAX_MEANINGFUL_DELTA_PCT = 4
const formatComparisonChange = (
  deltaPct: number | null | undefined,
  currentValue: number | null | undefined,
  priorValue: number | null | undefined,
  row: MetricComparisonRow | null,
): string => {
  const pct = deltaPct != null && Number.isFinite(deltaPct) ? deltaPct : null
  if (pct != null && (Math.abs(pct) <= MAX_MEANINGFUL_DELTA_PCT || row?.value_kind === 'ratio')) {
    return formatSignedPct(pct)
  }
  if (
    currentValue != null && Number.isFinite(currentValue) &&
    priorValue != null && Number.isFinite(priorValue)
  ) {
    const delta = currentValue - priorValue
    const sign = delta > 0 ? '+' : delta < 0 ? '−' : ''
    return `${sign}${formatCurrencyCompact(Math.abs(delta))}`
  }
  return formatSignedPct(pct)
}

type InstantComparisonFallback = {
  currentValue: number
  priorValue: number
  complete: boolean
  reason?: string | null
}

const aggregateTrendValues = (values: number[], row: MetricComparisonRow | null): number | null => {
  const finite = values.filter((value) => Number.isFinite(value))
  if (!finite.length) return null
  if (row?.value_kind === 'ratio') {
    return finite.reduce((sum, value) => sum + value, 0) / finite.length
  }
  return finite.reduce((sum, value) => sum + value, 0)
}

const trendComparisonFallback = (
  row: MetricComparisonRow | null,
  kind: MetricComparisonKind,
): InstantComparisonFallback | null => {
  const points = Array.isArray(row?.trend)
    ? row.trend
        .map((point) => ({
          ...point,
          value: Number(point?.value),
        }))
        .filter((point) => Number.isFinite(point.value))
    : []
  if (!points.length) return null
  if (kind === 'mom') {
    if (points.length < 2) return null
    const current = points[points.length - 1]?.value
    const prior = points[points.length - 2]?.value
    return Number.isFinite(current) && Number.isFinite(prior)
      ? { currentValue: current, priorValue: prior, complete: true, reason: 'Computed from monthly trend facts.' }
      : null
  }
  if (kind === 'l3m_vs_prior3m') {
    if (points.length < 6) return null
    const current = aggregateTrendValues(points.slice(-3).map((point) => point.value), row)
    const prior = aggregateTrendValues(points.slice(-6, -3).map((point) => point.value), row)
    return current != null && prior != null
      ? { currentValue: current, priorValue: prior, complete: true, reason: 'Computed from monthly trend facts.' }
      : null
  }
  return null
}

const instantComparisonRow = (label: string, row: MetricComparisonRow | null, kind: MetricComparisonKind) => {
  const comparison = pickComparison(row, kind)
  if (!comparison) {
    const fallback = trendComparisonFallback(row, kind)
    if (fallback) {
      const deltaAbs = fallback.currentValue - fallback.priorValue
      // Keep this a fraction (not a percentage): formatSignedPct/instantComparisonTone
      // scale by 100 themselves, matching the server delta_pct path below. The |prior|
      // denominator keeps the sign correct when the prior base is negative.
      const deltaPct = Math.abs(fallback.priorValue) > 0.000001 ? deltaAbs / Math.abs(fallback.priorValue) : null
      return {
        label,
        current: formatMetricComparisonValue(fallback.currentValue, row),
        prior: formatMetricComparisonValue(fallback.priorValue, row),
        change: formatComparisonChange(deltaPct, fallback.currentValue, fallback.priorValue, row),
        tone: instantComparisonTone(deltaPct),
        unavailableReason: fallback.complete ? null : fallback.reason || `${label} is based on incomplete monthly trend facts.`,
      }
    }
    return {
      label,
      current: '—',
      prior: '—',
      change: '—',
      tone: 'neutral' as const,
      unavailableReason: `${label} is not available in the current metric comparison contract.`,
    }
  }
  return {
    label,
    current: formatMetricComparisonValue(comparison?.current_value ?? null, row),
    prior: formatMetricComparisonValue(comparison?.prior_value ?? null, row),
    change: formatComparisonChange(comparison?.delta_pct ?? null, comparison?.current_value ?? null, comparison?.prior_value ?? null, row),
    tone: instantComparisonTone(comparison?.delta_pct ?? null),
    unavailableReason: comparison.complete === false ? `${label} is based on an incomplete comparison window.` : null,
  }
}

const buildInstantMetricComparisons = (row: MetricComparisonRow | null) => [
  instantComparisonRow('Current month', row, 'mom'),
  instantComparisonRow('Last 3 months', row, 'l3m_vs_prior3m'),
  instantComparisonRow('YTD', row, 'ytd'),
  instantComparisonRow('Prior year', row, 'yoy'),
]

const buildBridgeReadFallbackText = (bridgeReadContext: { context?: any } | null): string => {
  const context = bridgeReadContext?.context || {}
  const drivers = Array.isArray(context.drivers) ? context.drivers : []
  const rankedDrivers = drivers
    .filter((driver: any) => Number.isFinite(Number(driver?.net_income_effect)))
    .sort((a: any, b: any) => Math.abs(Number(b.net_income_effect)) - Math.abs(Number(a.net_income_effect)))
  const topDriver = rankedDrivers[0]
  const secondDriver = rankedDrivers[1]
  const netIncomeDelta = trimStr(context.net_income_delta_display)
  const currentNetIncome = trimStr(context.current_net_income_display)
  const comparisonLabel = trimStr(context.comparison_short_label || context.comparison_label).toLowerCase()
  const movement = Number(context.net_income_delta) >= 0 ? 'improved' : 'fell'
  const headline = currentNetIncome && netIncomeDelta
    ? `Net income ${movement} ${netIncomeDelta} ${comparisonLabel || 'for the selected period'} to ${currentNetIncome}.`
    : 'Net income moved for the selected period.'
  if (!topDriver) return `${headline} Open the bridge drivers to inspect the largest line movements.`
  const topEffect = trimStr(topDriver.net_income_effect_display)
  const topLine = `${trimStr(topDriver.label) || 'The largest driver'} had the largest net-income effect${topEffect ? ` (${topEffect})` : ''}.`
  const secondEffect = trimStr(secondDriver?.net_income_effect_display)
  const secondLine = secondDriver
    ? ` ${trimStr(secondDriver.label) || 'The next driver'} was next${secondEffect ? ` (${secondEffect})` : ''}.`
    : ''
  return `${headline} ${topLine}${secondLine} Click a bridge driver to inspect the underlying line.`
}

const bridgeReadMetricSnapshot = (
  primary: MetricComparisonResponse | null,
  fallback: MetricComparisonResponse | null,
  metricId: string,
  comparisonKind: MetricComparisonKind,
): { row: MetricComparisonRow | null; comparison: MetricComparison | null } => {
  const row = findMetricRow(primary, metricId) || findMetricRow(fallback, metricId)
  return { row, comparison: pickComparison(row, comparisonKind) }
}

/**
 * Assemble the same coarse oldest -> newest trajectory used by the CFO AI
 * metric tools. The comparison response has windowed prior/current values, not
 * a dense monthly series, so this is intentionally a compact evidence trail.
 */
const buildTrend = (row: MetricComparisonRow | null): { labels: string[]; trend: number[] } => {
  if (!row) return { labels: [], trend: [] }
  const ordered: Array<{ label: string; value: number | null }> = [
    { label: 'YoY prior', value: pickComparison(row, 'yoy')?.prior_value ?? null },
    { label: 'L12M prior', value: pickComparison(row, 'l12m_vs_prior12m')?.prior_value ?? null },
    { label: 'QoQ prior', value: pickComparison(row, 'qoq')?.prior_value ?? null },
    { label: 'L3M prior', value: pickComparison(row, 'l3m_vs_prior3m')?.prior_value ?? null },
    { label: 'MoM prior', value: pickComparison(row, 'mom')?.prior_value ?? null },
    { label: 'Latest', value: row.latest_value ?? null },
  ]
  const labels: string[] = []
  const trend: number[] = []
  for (const point of ordered) {
    if (point.value != null && Number.isFinite(point.value)) {
      labels.push(point.label)
      trend.push(point.value)
    }
  }
  return { labels, trend }
}

const buildInstantMetricTrend = (row: MetricComparisonRow | null, label: string) => {
  const monthlyTrend = Array.isArray(row?.trend)
    ? row.trend
      .filter((point) => point && point.value != null && Number.isFinite(point.value))
      .slice(-12)
    : []
  if (monthlyTrend.length) {
    return {
      label: `${monthlyTrend.length}-month trend`,
      points: monthlyTrend.map((point) => ({
        label: formatAsOf(point.period || point.label),
        period: point.period || null,
        value: formatMetricComparisonValue(point.value, row),
        rawValue: point.value,
      })),
      unavailable_reason: monthlyTrend.length >= 12
        ? null
        : `${label} has ${monthlyTrend.length} monthly trend point${monthlyTrend.length === 1 ? '' : 's'} available in the current metric comparison contract.`,
    }
  }
  const compact = buildTrend(row)
  return {
    label: compact.trend.length >= 12 ? '12-month trend' : 'Trend',
    points: compact.trend.map((value, index) => ({
      label: compact.labels[index] || `Point ${index + 1}`,
      value: formatMetricComparisonValue(value, row),
      rawValue: value,
    })),
    unavailable_reason: compact.trend.length >= 12
      ? null
      : `${label} is showing coarse comparison evidence because the Home metric contract does not yet include a dense 12-month monthly series.`,
  }
}

/**
 * Build a small trend series for a KPI sparkline out of the comparison engine
 * output. The comparison contract carries prior/current values per window but
 * not a full monthly series, so we assemble a coarse oldest -> newest trajectory
 * (YoY prior -> QoQ prior -> MoM prior -> latest) from whatever is populated.
 */
type SparklineShape = {
  linePath: string
  areaPath: string
  lastX: number
  lastY: number
  zeroY: number | null
}

const buildSmoothSparklinePath = (points: Array<[number, number]>): string => {
  if (!points.length) return ''
  let path = `M${points[0][0].toFixed(2)},${points[0][1].toFixed(2)}`
  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[i - 1] || points[i]
    const p1 = points[i]
    const p2 = points[i + 1]
    const p3 = points[i + 2] || p2
    const c1x = p1[0] + (p2[0] - p0[0]) / 6
    const c1y = p1[1] + (p2[1] - p0[1]) / 6
    const c2x = p2[0] - (p3[0] - p1[0]) / 6
    const c2y = p2[1] - (p3[1] - p1[1]) / 6
    path += `C${c1x.toFixed(2)},${c1y.toFixed(2)} ${c2x.toFixed(2)},${c2y.toFixed(2)} ${p2[0].toFixed(2)},${p2[1].toFixed(2)}`
  }
  return path
}

const buildSparklineShape = (row: MetricComparisonRow | null): SparklineShape | null => {
  if (!row) return null
  const ordered: Array<number | null> = [
    pickComparison(row, 'yoy')?.prior_value ?? null,
    pickComparison(row, 'l12m_vs_prior12m')?.prior_value ?? null,
    pickComparison(row, 'qoq')?.prior_value ?? null,
    pickComparison(row, 'l3m_vs_prior3m')?.prior_value ?? null,
    pickComparison(row, 'mom')?.prior_value ?? null,
    row.latest_value ?? null,
  ]
  const values = ordered.filter((v): v is number => v != null && Number.isFinite(v))
  if (values.length < 2) return null
  const min = Math.min(...values)
  const max = Math.max(...values)
  const span = max - min || 1
  const width = 100
  const height = 34
  const pad = 3
  const points = values.map((v, i): [number, number] => {
    const x = pad + (i * (width - 2 * pad)) / (values.length - 1)
    const y = height - pad - ((v - min) / span) * (height - 2 * pad)
    return [x, y]
  })
  const linePath = buildSmoothSparklinePath(points)
  const first = points[0]
  const last = points[points.length - 1]
  const zeroY = min < 0 && max > 0
    ? height - pad - ((0 - min) / span) * (height - 2 * pad)
    : null
  return {
    linePath,
    areaPath: `${linePath}L${last[0].toFixed(2)},${height}L${first[0].toFixed(2)},${height}Z`,
    lastX: last[0],
    lastY: last[1],
    zeroY,
  }
}

type KpiView = {
  def: { id: string; label: string }
  row: MetricComparisonRow | null
  display: string
  sparkline: SparklineShape | null
  deltaPct: number | null
  deltaAbs: number | null
  direction: 'up' | 'down' | 'flat'
}

const buildKpiView = (
  resp: MetricComparisonResponse | null,
  def: { id: string; label: string },
  comparisonKind: MetricComparisonKind,
): KpiView => {
  const row = findMetricRow(resp, def.id)
  const selected = pickComparison(row, comparisonKind)
  const display = row
    ? row.value_kind === 'ratio'
      ? formatRatio(row.latest_value)
      : formatCurrencyCompact(row.latest_value)
    : '—'
  const deltaPct = selected?.delta_pct ?? null
  const deltaAbs = selected?.delta_abs ?? null
  const direction: 'up' | 'down' | 'flat' =
    deltaPct == null || Math.abs(deltaPct) < 0.005 ? 'flat' : deltaPct > 0 ? 'up' : 'down'
  return { def, row, display, sparkline: buildSparklineShape(row), deltaPct, deltaAbs, direction }
}

export function CfoHomeBrief(props: CfoHomeBriefProps) {
  return (
    <AnalyticsProvider transport={props.appApi} distinctId={props.workbookId || null}>
      <CfoHomeBriefInner {...props} />
    </AnalyticsProvider>
  )
}

function CfoHomeBriefInner({
  appApi,
  workerClient = null,
  workbookId,
  selectedEndPeriod = null,
  onSelectedEndPeriodChange,
  periodOptions = [],
  comparisonKind = 'mom',
  onComparisonKindChange,
  comparisonOptions = [],
  apiSessionReady = true,
  onOpenCfoCore,
  onOpenCfoPresentation,
  onNavSelect,
  navProfile = 'full',
  runtimeProfile,
}: CfoHomeBriefProps) {
  const analytics = useAnalytics()
  const [activeJourneyId, setActiveJourneyId] = useState<string | null>(null)
  const activeJourney = useMemo(
    () => (activeJourneyId ? getJourneyDefinition(activeJourneyId) : null),
    [activeJourneyId],
  )
  // KPIs (period comparisons) and insights are independent server contracts and
  // resolve independently: each section owns its own data/loading/error state so
  // the faster call paints immediately instead of the whole surface waiting on
  // the slowest pole. The two fetches are still issued concurrently below.
  const [comparisons, setComparisons] = useState<MetricComparisonResponse | null>(null)
  const [currentSource, setCurrentSource] = useState<CfoCurrentStatementMetadata | null>(null)
  const localBridgeComparisons: MetricComparisonResponse | null = null
  const [comparisonsLoading, setComparisonsLoading] = useState(true)
  const [comparisonsError, setComparisonsError] = useState<string>('')
  const [insights, setInsights] = useState<CfoInsightsResponse | null>(null)
  const [insightsLoading, setInsightsLoading] = useState(true)
  const [insightsError, setInsightsError] = useState<string>('')
  const [addedCards, setAddedCards] = useState<Set<string>>(() => new Set())
  const [warmCacheState, setWarmCacheState] = useState<CfoHomeWarmState>(EMPTY_WARM_STATE)

  // Stable AI trace ids per insight card so feedback correlates to a card.
  const cardTraceIdsRef = useRef<Map<string, string>>(new Map())
  const traceIdForCard = useCallback((cardId: string): string => {
    const existing = cardTraceIdsRef.current.get(cardId)
    if (existing) return existing
    const next = newAiTraceId()
    cardTraceIdsRef.current.set(cardId, next)
    return next
  }, [])

  // Bind the agent's analytic tools (topk/groupby/correlation/outliers) to the
  // active CFO Core dataset. The CFO Core browser-DuckDB dataset is a persistent
  // ad-hoc dataset keyed `__fg_cfo_core_duckdb__` under a per-tab execution scope
  // (see QuickBooksConnectorDialog.tsx). We resolve the same tab execution scope
  // here and materialize an AI session *inside* that dataset's tab-local DuckDB
  // cache via `ai_materialize_session`, so its base/tidy tables read the live GL
  // detail table. The worker then opens the same execution-scoped cache for every
  // `ai_tool_*` RPC, so topk/groupby/correlation/outliers run over the live data.
  //
  // The session is cached per execution scope so repeated analytic tool calls in
  // a single run reuse one tidy table instead of re-materializing.
  const cfoCoreSessionRef = useRef<{ scopeId: string; sessionKey: string } | null>(null)
  const resolveAnalysisSession = useCallback<CfoAgentSessionResolver>(async () => {
    const wb = trimStr(workbookId)
    if (!workerClient || typeof workerClient.ai_materialize_session !== 'function' || !wb) {
      return null
    }
    const executionScopeId = buildTabExecutionScopeId({
      browserTabId: getBrowserTabId(),
      workbookScopeId: wb,
    })
    const cached = cfoCoreSessionRef.current
    if (cached && cached.scopeId === executionScopeId) {
      return { session_key: cached.sessionKey }
    }
    const sessionKey = makeAnalysisSessionKeyV1()
    const tableIdent = `"${QUICKBOOKS_GL_CREATOR_TABLE.replace(/"/g, '""')}"`
    const mat = await workerClient.ai_materialize_session({
      session_key: sessionKey,
      entity_key: `cfo-core:${wb}`,
      source: { kind: 'worksheet_sql', sql: `SELECT * FROM ${tableIdent}` },
      dataset_id: CFO_CORE_DUCKDB_DATASET_ID,
      executionScopeId,
      options: { max_base_rows: 200000, drop_if_exists: true },
    })
    const resolvedKey = trimStr(mat?.session?.session_key) || sessionKey
    cfoCoreSessionRef.current = { scopeId: executionScopeId, sessionKey: resolvedKey }
    return { session_key: resolvedKey }
  }, [workerClient, workbookId])

  // Publish the Brief's *live* comparison snapshot to the shared analysis modal
  // via a ref so the AI workspace grounds its answers in the exact numbers the
  // KPI cards render (and skips a redundant fetch). The ref is handed to
  // useRegisterCfoAnalysisPage below as `getComparisons`.
  const comparisonsRef = useRef<MetricComparisonResponse | null>(comparisons)
  comparisonsRef.current = comparisons
  const analysis = useCfoAnalysis()
  const cfoCoreDuckDbOpts = useMemo(() => {
    const wb = trimStr(workbookId)
    if (!wb) return null
    return {
      executionScopeId: buildTabExecutionScopeId({
        browserTabId: getBrowserTabId(),
        workbookScopeId: wb,
      }),
      persistentAdHoc: true,
      persistentDatasetId: CFO_CORE_DUCKDB_DATASET_ID,
    }
  }, [workbookId])
  const { openGlDetail: openGlDetailInline, glDetailModal } = useCfoInlineGlDetail({
    appApi,
    workerClient,
    workbookId,
    duckDbOpts: cfoCoreDuckDbOpts,
    // handleNavSelect is declared later in this component; the hook reads its
    // options per-call, so a thin wrapper over the prop is enough here.
    onNavSelect: (navId) => onNavSelect?.(navId),
    period: insights?.as_of || comparisons?.as_of || selectedEndPeriod || null,
  })
  const [bridgeRead, setBridgeRead] = useState<{
    key: string
    status: 'loading' | 'ready' | 'error'
    text: string
  } | null>(null)

  useEffect(() => {
    if (!CFO_HOME_WARM_HOME_ARTIFACTS_ON_LOAD) return
    const wb = trimStr(workbookId)
    if (!apiSessionReady || !wb || !workerClient) return
    if (
      typeof workerClient.duckdb_create_table_from_parquet_urls !== 'function'
      || typeof workerClient.duckdb_create_temp_table_from_rows !== 'function'
      || typeof workerClient.duckdb_run_sql !== 'function'
    ) {
      return
    }
    const executionScopeId = buildTabExecutionScopeId({
      browserTabId: getBrowserTabId(),
      workbookScopeId: wb,
    })
    const key = `${executionScopeId}:home-warm:v4`
    const entry = getCfoHomeWarmEntry(key)
    const log = (event: string, payload?: Record<string, any>) => {
      try {
        console.info('[cfo-home-warm]', event, payload || {})
      } catch {}
    }
    const subscriber: CfoHomeWarmSubscriber = (next) => setWarmCacheState(next)
    entry.subscribers.add(subscriber)
    setWarmCacheState(cloneWarmState(entry.state))

    if (entry.promise) {
      log('warm_reuse_inflight', { workbook_id: wb, key, status: entry.state.status })
      return () => {
        entry.subscribers.delete(subscriber)
      }
    }

    if (entry.state.status === 'ready') {
      log('warm_reuse_ready', {
        workbook_id: wb,
        key,
        tables: entry.state.artifacts.filter((artifact) => artifact.status === 'ready').length,
      })
      return () => {
        entry.subscribers.delete(subscriber)
      }
    }

    setCfoHomeWarmEntryState(entry, { ...cloneWarmState(EMPTY_WARM_STATE), status: 'warming' })
    entry.promise = warmCfoHomeAiArtifacts(appApi, workerClient, wb, log, (next) => {
      setCfoHomeWarmEntryState(entry, next)
    }, CFO_HOME_WARM_AI_ARTIFACTS_ON_LOAD)
      .catch((error: any) => {
        const message = error?.message || String(error)
        patchCfoHomeWarmEntryState(entry, (prev) => ({
          ...prev,
          status: 'failed',
          error: message,
          current_artifact: null,
          artifacts: prev.artifacts.map((artifact) =>
            artifact.status === 'downloading' ? { ...artifact, status: 'failed' } : artifact,
          ),
        }))
        log('warm_failed', {
          workbook_id: wb,
          error: message,
        })
      })
      .finally(() => {
        entry.promise = null
      })

    return () => {
      entry.subscribers.delete(subscriber)
    }
  }, [apiSessionReady, appApi, workerClient, workbookId])

  // Bumped when an on-demand AI refresh completes, to refetch the home's comparisons/insights.
  const reloadKey = workbookId || ''
  useEffect(() => {
    let cancelled = false
    const wb = trimStr(workbookId)
    if (!wb) {
      setComparisonsLoading(false)
      setInsightsLoading(false)
      const noWorkbook = 'No workbook is bound to this view.'
      setComparisonsError(noWorkbook)
      setInsightsError(noWorkbook)
      return
    }
    if (!apiSessionReady) {
      setComparisonsLoading(true)
      setInsightsLoading(true)
      setComparisonsError('')
      setInsightsError('')
      return
    }
    setComparisonsLoading(true)
    setInsightsLoading(true)
    setComparisonsError('')
    setInsightsError('')

    const trackLoaded = (insightCount: number, attentionCount: number, asOf: string | null | undefined, source: string) => {
      analytics.track({
        event: 'cfo_home_brief_loaded',
        surface: SURFACE,
        workbook_id: wb,
        props: {
          attention_count: attentionCount,
          insight_count: insightCount,
          as_of: asOf,
          source,
        },
      })
    }

    const loadLegacyContracts = () => {
      // Issue both fetches concurrently but resolve independently so the KPI strip
      // and the insight rail each leave their loading state the moment their own
      // server contract returns — neither blocks on the other's latency.
      const loadComparisons = (appApi.getCfoMetricComparisons || appApi.getQuickBooksMetricComparisons)?.bind(appApi)
      const loadInsights = (appApi.getCfoInsights || appApi.getQuickBooksInsights)?.bind(appApi)
      if (typeof loadComparisons !== 'function' || typeof loadInsights !== 'function') {
        setComparisonsError('The KPI reporting contract is not available in this client session.')
        setInsightsError('The insight reporting contract is not available in this client session.')
        setComparisonsLoading(false)
        setInsightsLoading(false)
        return
      }
      loadComparisons(wb, {
          asOf: selectedEndPeriod,
          grain: 'month',
          metrics: HOME_COMPARISON_METRIC_IDS,
        })
        .then((cmp) => {
          if (cancelled) return
          setComparisons(cmp)
        })
        .catch((err: any) => {
          if (cancelled) return
          setComparisonsError(trimStr(err?.message || err) || 'Could not load the KPIs.')
        })
        .finally(() => {
          if (!cancelled) setComparisonsLoading(false)
        })

      loadInsights(wb, { asOf: selectedEndPeriod, grain: 'month' })
        .then((ins) => {
          if (cancelled) return
          const filtered = filterHomeInsights(ins)
          setInsights(filtered)
          trackLoaded(filtered.cards.length, filtered.attention_count, filtered.as_of, 'legacy_contracts')
        })
        .catch((err: any) => {
          if (cancelled) return
          setInsightsError(trimStr(err?.message || err) || 'Could not load the insights.')
        })
        .finally(() => {
          if (!cancelled) setInsightsLoading(false)
        })
    }

    const loadHome = (appApi.getCfoHome || appApi.getQuickBooksCfoHome)?.bind(appApi)
    setCurrentSource(null)
    if (typeof loadHome === 'function') {
      loadHome(wb, { asOf: selectedEndPeriod, grain: 'month' })
        .then((home) => {
          if (cancelled) return
          setCurrentSource(home.current_source || null)
          setComparisons(home.comparisons)
          const homeInsights = filterHomeInsights(cfoHomeInsightsFromContract(home))
          setInsights(homeInsights)
          trackLoaded(homeInsights.cards.length, homeInsights.attention_count, homeInsights.as_of, 'cfo_home_contract')
          setComparisonsLoading(false)
          setInsightsLoading(false)
        })
        .catch((err: any) => {
          if (cancelled) return
          const message = trimStr(err?.message || err) || 'Could not load the CFO Home contract.'
          setComparisonsError(message)
          setInsightsError(message)
          setComparisonsLoading(false)
          setInsightsLoading(false)
        })
    } else {
      loadLegacyContracts()
    }

    return () => {
      cancelled = true
    }
    // analytics is a stable singleton wrapper; reload only on workbook change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiSessionReady, appApi, reloadKey, selectedEndPeriod])

  const kpis = useMemo(() => KPI_DEFS.map((def) => buildKpiView(comparisons, def, comparisonKind)), [comparisonKind, comparisons])
  const bridgeComparison = bridgeComparisonOption(comparisonKind)
  const bridge = useMemo(
    () => serverWaterfallBridge(comparisons, comparisonKind),
    [comparisons, comparisonKind],
  )
  const localCogsComparison = pickComparison(findMetricRow(localBridgeComparisons, 'cost_of_sales'), comparisonKind)
  const bridgeCogsCostDelta = bridge?.costDelta ?? localCogsComparison?.delta_abs ?? 0
  const bridgePriorLabel = bridge ? formatBridgePeriod(bridge.priorPeriodStart, bridge.priorPeriodEnd) : ''
  const bridgeLatestLabel = bridge ? formatBridgePeriod(bridge.latestPeriodStart, bridge.latestPeriodEnd) : ''
  const bridgeRangeLabel = bridge ? `${bridgePriorLabel} → ${bridgeLatestLabel}` : ''
  const bridgePromptSuffix = bridge
    ? ` Use the selected comparison window: ${bridgeComparison.label} (${bridgeRangeLabel}).`
    : ` Use the selected comparison window: ${bridgeComparison.label}.`
  // One shared net-income-bridge driver builder (Revenue / COGS / Opex with the
  // favorability-signed net-income effect) used by the auto bridge read, the
  // chart-level "analyze the bridge" open, and the single-bar driver click, so
  // the three driver payloads can never drift in sign or shape.
  const bridgeDrivers = useMemo(() => {
    if (!bridge) return []
    const revenue = bridgeReadMetricSnapshot(comparisons, localBridgeComparisons, 'revenue', comparisonKind)
    const cogs = bridgeReadMetricSnapshot(comparisons, localBridgeComparisons, 'cost_of_sales', comparisonKind)
    const opex = bridgeReadMetricSnapshot(comparisons, localBridgeComparisons, 'operating_expenses', comparisonKind)
    return [
      { id: 'revenue', label: 'Revenue', snapshot: revenue, netIncomeEffect: bridge.revenueDelta },
      { id: 'cost_of_sales', label: 'COGS', snapshot: cogs, netIncomeEffect: -bridgeCogsCostDelta },
      { id: 'operating_expenses', label: 'Opex', snapshot: opex, netIncomeEffect: -bridge.expenseDelta },
    ].map((driver) => ({
      id: driver.id,
      label: driver.label,
      current_value: driver.snapshot.comparison?.current_value ?? driver.snapshot.row?.latest_value ?? null,
      prior_value: driver.snapshot.comparison?.prior_value ?? null,
      metric_delta_abs: driver.snapshot.comparison?.delta_abs ?? null,
      metric_delta_pct: driver.snapshot.comparison?.delta_pct ?? null,
      current_pct_of_sales: driver.snapshot.comparison?.current_pct_of_sales ?? null,
      prior_pct_of_sales: driver.snapshot.comparison?.prior_pct_of_sales ?? null,
      net_income_effect: driver.netIncomeEffect,
      net_income_effect_display: `${driver.netIncomeEffect >= 0 ? '+' : '-'}${formatCurrencyCompact(Math.abs(driver.netIncomeEffect))}`,
      current_value_display: driver.snapshot.comparison?.current_value != null
        ? formatCurrencyCompact(driver.snapshot.comparison.current_value)
        : null,
      prior_value_display: driver.snapshot.comparison?.prior_value != null
        ? formatCurrencyCompact(driver.snapshot.comparison.prior_value)
        : null,
      metric_delta_abs_display: driver.snapshot.comparison?.delta_abs != null
        ? `${driver.snapshot.comparison.delta_abs >= 0 ? '+' : '-'}${formatCurrencyCompact(Math.abs(driver.snapshot.comparison.delta_abs))}`
        : null,
    }))
  }, [bridge, bridgeCogsCostDelta, comparisonKind, comparisons, localBridgeComparisons])

  // Prior NI → This NI anchors for the card's hero cause-bridge.
  const bridgeAnchors = useMemo(
    () =>
      bridge
        ? {
          start: { label: 'Prior NI', value: bridge.priorNetIncome },
          end: { label: 'This NI', value: bridge.latestNetIncome },
        }
        : null,
    [bridge],
  )

  const bridgeReadContext = useMemo(() => {
    if (!bridge) return null
    // The auto bridge read must narrate the same canonical comparison payload
    // that draws the waterfall. Local DuckDB comparisons remain useful as a
    // fallback while cache warms, but they can be on a different artifact/sign
    // basis and must not outrank the rendered chart's source of truth.
    const netIncome = bridgeReadMetricSnapshot(comparisons, localBridgeComparisons, 'net_income', comparisonKind)
    const drivers = bridgeDrivers
    const context = {
      comparison_kind: comparisonKind,
      comparison_label: bridgeComparison.label,
      comparison_short_label: bridgeComparison.shortLabel,
      range_label: bridgeRangeLabel,
      prior_period_start: bridge.priorPeriodStart,
      prior_period_end: bridge.priorPeriodEnd,
      current_period_start: bridge.latestPeriodStart,
      current_period_end: bridge.latestPeriodEnd,
      prior_net_income: bridge.priorNetIncome,
      prior_net_income_display: formatCurrencyCompact(bridge.priorNetIncome),
      current_net_income: bridge.latestNetIncome,
      current_net_income_display: formatCurrencyCompact(bridge.latestNetIncome),
      net_income_delta: bridge.latestNetIncome - bridge.priorNetIncome,
      net_income_delta_display: `${bridge.latestNetIncome - bridge.priorNetIncome >= 0 ? '+' : '-'}${formatCurrencyCompact(Math.abs(bridge.latestNetIncome - bridge.priorNetIncome))}`,
      net_income_snapshot: {
        current_value: netIncome.comparison?.current_value ?? netIncome.row?.latest_value ?? bridge.latestNetIncome,
        prior_value: netIncome.comparison?.prior_value ?? bridge.priorNetIncome,
        delta_abs: netIncome.comparison?.delta_abs ?? bridge.latestNetIncome - bridge.priorNetIncome,
        delta_pct: netIncome.comparison?.delta_pct ?? null,
        current_value_display: formatCurrencyCompact(netIncome.comparison?.current_value ?? netIncome.row?.latest_value ?? bridge.latestNetIncome),
        prior_value_display: formatCurrencyCompact(netIncome.comparison?.prior_value ?? bridge.priorNetIncome),
        delta_abs_display: `${(netIncome.comparison?.delta_abs ?? bridge.latestNetIncome - bridge.priorNetIncome) >= 0 ? '+' : '-'}${formatCurrencyCompact(Math.abs(netIncome.comparison?.delta_abs ?? bridge.latestNetIncome - bridge.priorNetIncome))}`,
      },
      drivers,
    }
    return {
      key: JSON.stringify({
        prompt: 'bridge-read-v2',
        workbookId,
        comparisonKind,
        prior: bridge.priorNetIncome,
        current: bridge.latestNetIncome,
        revenue: bridge.revenueDelta,
        cogs: bridgeCogsCostDelta,
        opex: bridge.expenseDelta,
        range: bridgeRangeLabel,
      }),
      context,
    }
  }, [bridge, bridgeCogsCostDelta, bridgeComparison.label, bridgeComparison.shortLabel, bridgeRangeLabel, bridgeDrivers, comparisonKind, comparisons, localBridgeComparisons, workbookId])

  // Open the analysis modal on the net-income bridge in general (chart-level).
  const openNetIncomeBridge = useCallback(() => {
    if (!bridge) return
    const netIncomeRow = findMetricRow(comparisons, 'net_income') || findMetricRow(localBridgeComparisons, 'net_income')
    analysis.openAnalysis({
      metricId: 'net_income',
      metricLabel: 'Net income',
      comparison: comparisonKind,
      sourceComponent: 'net_income_bridge',
      sourceTables: ['fg_cfo_ai_statement_monthly_activity'],
      selection: {
        intent: 'explain_selected_change',
        source_grain: 'bridge_total',
        metric_id: 'net_income',
        metric_label: 'Net income',
        // Ranked Revenue/COGS/Opex drivers (favorability-signed) feed the card's
        // hero bridge + ranked driver list; the deterministic builder derives the
        // verdict, concentration insight, and prescribed action from them — so we
        // no longer hand-author a tautological instant_card_insights string here.
        drivers: bridgeDrivers,
        bridge_anchors: bridgeAnchors,
        comparison_kind: comparisonKind,
        comparison_label: bridgeComparison.label,
        prior_period_start: bridge.priorPeriodStart,
        prior_period_end: bridge.priorPeriodEnd,
        current_period_start: bridge.latestPeriodStart,
        current_period_end: bridge.latestPeriodEnd,
        instant_card_trend: buildInstantMetricTrend(netIncomeRow, 'Net income'),
        instant_card_comparisons: buildInstantMetricComparisons(netIncomeRow),
      },
      visual: {
        kind: 'value',
        label: 'Net income',
        display: formatCurrencyCompact(bridge.latestNetIncome),
        sub: `${bridgeComparison.shortLabel}: ${formatCurrencyCompact(bridge.priorNetIncome)} → ${formatCurrencyCompact(bridge.latestNetIncome)}`,
        value: bridge.latestNetIncome,
      },
      seedPrompt:
        `Why did net income change for ${bridgeComparison.phrase}? Walk the profit bridge for that selected window, answer one layer, and tell me the next specific data to inspect.${bridgePromptSuffix}`,
    })
  }, [analysis, bridge, bridgeAnchors, bridgeComparison, bridgeDrivers, bridgePromptSuffix, comparisonKind, comparisons, localBridgeComparisons])
  const attentionCount = insights?.attention_count ?? 0
  const asOfLabel = useMemo(() => formatAsOf(insights?.as_of || comparisons?.as_of || null), [insights, comparisons])

  useEffect(() => {
    const canonicalAsOf = trimStr(insights?.as_of || comparisons?.as_of || '')
    if (!selectedEndPeriod && canonicalAsOf) onSelectedEndPeriodChange?.(canonicalAsOf)
  }, [comparisons, insights, onSelectedEndPeriodChange, selectedEndPeriod])

  // Prewarm the ratio library for the resolved period as soon as the brief
  // loads, so clicking a ratio insight embeds the full ratio card instantly
  // (no per-click fetch). Keyed to the same period the analysis modal requests.
  useEffect(() => {
    prefetchQuickBooksRatios(appApi, workbookId, insights?.as_of || comparisons?.as_of || selectedEndPeriod || null)
  }, [appApi, workbookId, insights?.as_of, comparisons?.as_of, selectedEndPeriod])

  // Publish Home context so the shared analysis modal can ground the agent and
  // seed evidence from clicked KPIs / the net-income bridge.
  useRegisterCfoAnalysisPage({
    pageId: 'home',
    activeSection: 'cfo_home',
    duckDbOpts: cfoCoreDuckDbOpts,
    getComparisons: () => comparisonsRef.current,
    period: insights?.as_of || comparisons?.as_of || null,
    comparison: comparisonKind,
    asOfLabel,
    suggestedPrompts: CFO_HOME_SUGGESTED_PROMPTS,
    openGlDetail: openGlDetailInline,
  })
  // The Brief's visible KPIs, insight cards, bridge text, and page copilot
  // evidence are served by canonical server CFO contracts. Browser DuckDB is
  // reserved for explicit local/ad hoc analysis surfaces, not Home first paint.
  const requiresHomeCache = CFO_HOME_WARM_HOME_ARTIFACTS_ON_LOAD && Boolean(workerClient)
  const comparisonsBusy = comparisonsLoading
  // The full presentation payload, fetched AFTER the brief has rendered. It is
  // what lets the statement-variance, bridge, working-capital and A/R families
  // resolve here — without it the brief can only draw trend/progress/dual-axis.
  // Deliberately deferred and best-effort: the home page must paint on the
  // insight contract alone, and a failure here costs richer highlights, never
  // the brief itself.
  const highlightPayload = useCfoPresentationEnrichment({
    appApi,
    workbookId,
    requestedPeriod: insights?.as_of || selectedEndPeriod || null,
    source: currentSource,
    ready: !insightsLoading && Boolean(insights?.cards?.length),
  })

  // Key Highlights run the SAME registry, evidence builder and selector as the
  // board pack, so the brief and the pack cannot disagree about what matters.
  const briefHighlights = useMemo(
    () => selectBriefHighlights({
      insights,
      comparisons,
      presentation: highlightPayload,
      dataThrough: insights?.as_of ?? null,
      hiddenDetectors: [...HOME_HIDDEN_INSIGHT_DETECTORS],
    }),
    [insights, comparisons, highlightPayload],
  )

  const insightsBusy = insightsLoading

  useEffect(() => {
    if (!bridgeReadContext) {
      if (bridgeRead) setBridgeRead(null)
      return
    }
    if (comparisonsBusy) return
    if (bridgeRead?.key === bridgeReadContext.key) return
    setBridgeRead({
      key: bridgeReadContext.key,
      status: 'ready',
      text: buildBridgeReadFallbackText(bridgeReadContext),
    })
  }, [bridgeRead, bridgeRead?.key, bridgeReadContext, comparisonsBusy])

  const warmReadyCount = warmCacheState.artifacts.filter((artifact) => artifact.status === 'ready').length
  const warmTotalCount = warmCacheState.artifacts.length
  const warmHomeReadyCount = warmCacheState.artifacts
    .filter((artifact) => artifact.phase === 'home' && artifact.status === 'ready')
    .length
  const warmHomeTotalCount = warmCacheState.artifacts.filter((artifact) => artifact.phase === 'home').length
  const warmCurrent = warmCacheState.current_artifact
    ? warmCacheState.artifacts.find((artifact) => artifact.id === warmCacheState.current_artifact)
    : null
  const aiSemanticViewsReady = warmCacheState.artifacts.some((artifact) => artifact.id === 'ai_semantic_views' && artifact.status === 'ready')
  const aiGlViewReady = warmCacheState.artifacts.some((artifact) => artifact.id === 'ai_gl_view' && artifact.status === 'ready')
  const warmCacheTitle = useMemo(() => {
    if (!warmCacheState.artifacts.length) return 'Preparing artifact cache'
    const line = (label: string, statuses: CfoHomeWarmArtifactStatus[]) => {
      const items = warmCacheState.artifacts
        .filter((artifact) => statuses.includes(artifact.status))
        .map((artifact) => artifact.label)
      return `${label}: ${items.length ? items.join(', ') : 'none'}`
    }
    return [
      line('Ready', ['ready']),
      line('Downloading', ['downloading']),
      line('Pending', ['pending']),
      line('Failed', ['failed']),
    ].join('\n')
  }, [warmCacheState.artifacts])
  const warmCacheLabel = !requiresHomeCache
    ? 'Cache optional'
    : warmCacheState.status === 'ready'
      ? aiGlViewReady
        ? 'AI data ready'
        : aiSemanticViewsReady
          ? 'AI summary ready · GL pending'
          : 'AI views not ready'
      : warmCacheState.status === 'home_ready'
        ? aiSemanticViewsReady
          ? `AI summary ready · ${warmReadyCount}/${warmTotalCount}`
          : `Home ready · AI ${warmReadyCount}/${warmTotalCount}`
        : warmCacheState.status === 'failed'
          ? 'Cache failed'
          : warmCurrent
            ? `Preparing ${warmCurrent.label}`
            : warmHomeTotalCount
              ? `Home cache ${warmHomeReadyCount}/${warmHomeTotalCount}`
              : 'Preparing cache'

  // Compact "glance": a single specific net-income sentence + the real attention
  // items as clickable chips (replaces the generic headline/paragraph/AI tag/CTAs).
  const glance = useMemo(() => {
    const ni = kpis.find((k) => k.def.id === 'net_income') || null
    const pct = ni?.deltaPct ?? null
    const flat = pct == null || Math.abs(pct) < 0.005
    return {
      dirWord: flat ? 'held at' : (pct as number) < 0 ? 'fell' : 'rose',
      tone: flat ? 'flat' : (pct as number) < 0 ? 'down' : 'up',
      pctText: flat ? '' : `${Math.abs((pct as number) * 100).toFixed(1)}% `,
      display: ni?.display || (bridge ? formatCurrencyCompact(bridge.latestNetIncome) : '—'),
      chips: (insights?.cards || []).slice(0, 3),
    }
  }, [kpis, bridge, insights])

  const handleExplain = useCallback(
    (card: InsightCard) => {
      analytics.track({
        event: 'cfo_home_insight_explain',
        surface: SURFACE,
        workbook_id: workbookId || null,
        ai_trace_id: traceIdForCard(card.id),
        props: { detector: card.detector, metric_id: card.impact.metric_id },
      })
      const metricId = trimStr(card.impact?.metric_id)
      const metricRow = metricId ? findMetricRow(comparisons, metricId) || findMetricRow(localBridgeComparisons, metricId) : null
      const metricLabel = metricId
        ? metricId.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
        : trimStr(card.tags?.[0]) || 'Insight'
      const selectedComparison = pickComparison(metricRow, comparisonKind)
      const currentDisplay = selectedComparison?.current_value != null
        ? formatMetricComparisonValue(selectedComparison.current_value, metricRow)
        : card.impact?.value != null
          ? formatCurrencyCompact(card.impact.value)
          : 'Selected'
      const changeDisplay = selectedComparison?.delta_pct != null
        ? formatComparisonChange(selectedComparison.delta_pct, selectedComparison.current_value, selectedComparison.prior_value, metricRow)
        : card.impact?.value != null
          ? formatCurrencyCompact(card.impact.value)
          : '—'
      const period = insights?.as_of || comparisons?.as_of || null
      // Plain-language + always-chartable seeds, so the Explain modal leads with a
      // gauge and a "what this is / why it matters" read instead of an apology.
      const explainer = explainerForCard(card)
      const band = healthBandFromCard(card)
      const valueText = trimStr(card.value_display) || band?.display || formatImpact(card)
      const serverTrend = Array.isArray(card.trend) ? card.trend : []
      const bandUnit = band?.unit || (card.impact?.unit === 'usd' ? 'currency' : 'ratio')
      const instantTrend = serverTrend.length >= 2
        ? {
            label: `${Math.min(serverTrend.length, 12)}-month trend`,
            points: serverTrend.slice(-12).map((point) => ({
              label: formatAsOf(point.period),
              period: point.period,
              value: formatByUnit(point.value, bandUnit),
              rawValue: point.value,
            })),
            unavailable_reason: null,
          }
        : buildInstantMetricTrend(metricRow, metricLabel)
      analysis.openAnalysis({
        metricId: metricId || null,
        metricLabel,
        sourceComponent: 'insight_card',
        selection: {
          insight_card: card,
          metric_id: metricId || null,
          metric_label: metricLabel,
          comparison_kind: comparisonKind,
          instant_card_trend: instantTrend,
          instant_card_comparisons: buildInstantMetricComparisons(metricRow),
          instant_card_health_band: band
            ? {
                value: band.value,
                healthyMin: band.healthyMin,
                healthyMax: band.healthyMax,
                higherIsBetter: band.higherIsBetter,
                unit: band.unit,
                assessment: band.assessment,
                display: valueText,
                minLabel: band.minLabel,
                maxLabel: band.maxLabel,
              }
            : null,
          instant_card_explain: {
            friendlyName: explainer.friendlyName,
            whatItIs: explainer.whatItIs,
            whyItMatters: explainer.whyItMatters,
            goodRange: explainer.goodRange,
            category: explainer.category,
            isDataQuality: explainer.isDataQuality,
            replacementLabel: explainer.replacementLabel,
          },
          // The insight headline IS the verdict — hand it to the card verbatim
          // so it never re-derives a vacuous "<metric> moved MoM." next to an
          // insight body that talks about something else.
          instant_card_verdict: {
            grade: SEVERITY_GRADE[card.severity] ?? 'neutral',
            headline: card.headline,
            magnitudeLine: null,
          },
          instant_card_insights: [
            card.body || card.headline,
          ],
          instant_card_prescriptive_actions: [
            `Inspect the largest ${metricLabel} driver and decide whether it changes this month's operating plan.`,
          ],
          instant_card_next_steps: [
            {
              kind: 'drilldown',
              label: `Break down ${metricLabel}`,
              prompt: `Break down ${metricLabel} using bounded account, customer, service-line, or child-detail packets when available.`,
              target: { surface: 'cfo_home', metricId: metricId || null },
            },
            {
              kind: 'compare',
              label: 'Compare periods',
              prompt: `Compare ${metricLabel} across current month, last 3 months, YTD, and prior year.`,
              target: { surface: 'cfo_home', metricId: metricId || null },
            },
            {
              kind: 'transactions',
              label: 'Inspect detail',
              prompt: `Open bounded transaction detail for ${metricLabel}.`,
              target: { surface: 'cfo_gl_detail', metricId: metricId || null },
            },
          ],
          instant_card_change_explanations: [
            {
              periodKind: 'current_month',
              label: 'Current month',
              status: metricRow ? 'ready' : 'partial',
              current: currentDisplay,
              prior: selectedComparison?.prior_value != null ? formatMetricComparisonValue(selectedComparison.prior_value, metricRow) : '—',
              change: changeDisplay,
              headline: card.body || card.headline,
              // No fabricated driver: the insight headline is a verdict, not a
              // decomposition, and pairing it with the metric's Δ% read as
              // nonsense (e.g. a confidence warning "driving" +12,072%). The
              // modal fetches the real statement-level drivers and grafts them
              // in (see shouldFetchCfoNextLevelBreakdown).
              drivers: [],
              confidence: metricRow ? 'high' : 'medium',
              caveats: metricRow ? [] : ['This insight did not map to a full metric comparison row.'],
              recommendedAction: `Inspect the largest ${metricLabel} driver before changing the monthly narrative.`,
            },
          ],
        },
        // Seed the instant ratio-health gauge + grounding from data already on
        // screen, so the answer leads with a visual and never waits on warming.
        visual: ratioHealthVisualFromInsightCard(card),
        grounding: groundingFromInsightCard(card, period),
        label: card.headline,
        seedPrompt: [
          `Give a short, grounded read on this CFO Home insight: ${card.headline}.`,
          'Treat workspace_context.grounding (value, healthy range, assessment, interpretation) as authoritative — it is what the user sees. In 1-2 sentences say whether it is healthy and the single main driver, then one concrete next step. Do not restate the card or hedge about local data.',
        ].join(' '),
      })
    },
    [analytics, analysis, comparisonKind, comparisons, insights, traceIdForCard, workbookId],
  )

  const handleShowDetail = useCallback(
    (card: InsightCard) => {
      analytics.track({
        event: 'cfo_home_insight_drill',
        surface: SURFACE,
        workbook_id: workbookId || null,
        ai_trace_id: traceIdForCard(card.id),
        props: { detector: card.detector, drill_tab: card.drill_target.tab, drill_view: card.drill_target.view },
      })
      onOpenCfoCore?.(card.drill_target)
    },
    [analytics, onOpenCfoCore, traceIdForCard, workbookId],
  )

  const handleNavSelect = useCallback(
    (itemId: string) => {
      // Unified-surface router: delegate the whole rail to the host so it can
      // render native interior pages in-shell (and escape-hatch to the classic
      // tab for sections without a calm page yet).
      if (onNavSelect) {
        analytics.track({
          event: 'cfo_home_nav_select',
          surface: SURFACE,
          workbook_id: workbookId || null,
          props: { item: itemId },
        })
        onNavSelect(itemId)
        return
      }
      // 'home' is the Brief itself — it is the active rail item, so selecting it
      // is a no-op beyond scrolling back to the top of the surface.
      if (itemId === 'home') {
        document.querySelector<HTMLElement>('.cfo-shell-main')?.scrollTo({ top: 0 })
        return
      }
      analytics.track({
        event: 'cfo_home_nav_select',
        surface: SURFACE,
        workbook_id: workbookId || null,
        props: { item: itemId },
      })
      if (itemId === 'presentations') {
        onOpenCfoPresentation?.()
        return
      }
      onOpenCfoCore?.(NAV_DRILL_TARGETS[itemId] ?? { tab: itemId, view: null, params: {} })
    },
    [analytics, onNavSelect, onOpenCfoCore, onOpenCfoPresentation, workbookId],
  )

  const handleAddToReport = useCallback(
    (card: InsightCard) => {
      analytics.track({
        event: 'cfo_home_insight_add_to_report',
        surface: SURFACE,
        workbook_id: workbookId || null,
        ai_trace_id: traceIdForCard(card.id),
        props: { detector: card.detector, metric_id: card.impact.metric_id },
      })
      if (!workbookId) return
      setAddedCards((prev) => {
        const next = new Set(prev)
        next.add(card.id)
        return next
      })
      // Append the insight to the shared presentation config via the
      // presentation block primitive (ServerApiClient.appendQuickBooksCfoPresentationBlock).
      void appApi
        .appendQuickBooksCfoPresentationBlock(workbookId, {
          title: trimStr(card.headline) || 'Insight',
          text: trimStr(card.body),
          kind: 'text',
          section: 'actions',
        })
        .catch(() => {
          setAddedCards((prev) => {
            const next = new Set(prev)
            next.delete(card.id)
            return next
          })
        })
    },
    [analytics, appApi, traceIdForCard, workbookId],
  )

  // The Brief now uses the same linked shell as the rest of the calm CFO pages;
  // the Brief-specific dashboard flows through the shell's content slots, and the
  // AI workspace is the shared CfoAnalysisProvider/CfoAnalysisModal (opened via
  // the shell's Ask affordance and the page's explainable charts/values).
  const briefCacheState = requiresHomeCache
    ? {
        tone:
          warmCacheState.status === 'ready'
            ? ('ready' as const)
            : warmCacheState.status === 'failed'
              ? ('failed' as const)
              : ('idle' as const),
        label: warmCacheLabel,
        title: warmCacheTitle,
      }
    : null

  const briefOverlay = activeJourney ? (
    <JourneyShell
      journey={activeJourney}
      appApi={appApi}
      workerClient={workerClient}
      workbookId={workbookId}
      onClose={() => setActiveJourneyId(null)}
      onOpenPresentation={onOpenCfoPresentation}
      onOpenCfoCore={onOpenCfoCore}
    />
  ) : null

  return (
    <CfoPageShell
      activeNavId="home"
      onNavSelect={handleNavSelect}
      brandLabel="CFO Core"
      brandSub={asOfLabel}
      crumb={
        <>
          <b>Home</b> · Monthly Brief
        </>
      }
      asOfLabel={asOfLabel}
      periodOptions={periodOptions}
      periodValue={selectedEndPeriod || comparisons?.as_of || insights?.as_of || undefined}
      onPeriodChange={onSelectedEndPeriodChange}
      periodAriaLabel="Select CFO Core end period"
      comparisonLabel={bridgeComparison.label}
      comparisonOptions={comparisonOptions}
      comparisonValue={comparisonKind}
      onComparisonChange={(value) => onComparisonKindChange?.(value as MetricComparisonKind)}
      comparisonAriaLabel="Select CFO Core comparison period"
      cacheState={briefCacheState}
      askPlaceholder="Ask anything about your finances…"
      onAsk={() => analysis.openAsk()}
      overlay={briefOverlay}
      dataSurface={SURFACE}
      navProfile={navProfile}
      runtimeProfile={runtimeProfile}
    >
      {glDetailModal}
      <CfoCurrentSourceNotice source={currentSource} />
      <ol className="guide cfo-home-guide">
        <li className="step rise" style={{ animationDelay: '.02s' }}>
          <span className="node" aria-hidden="true">
            <b>01</b>
          </span>
          <div className="step-head">
            <span className="step-meta">The glance</span>
          </div>

          <section className="hero cfo-home-brief-hero" id="cfo-home-hero">
            <div className="cfo-home-glance">
              {insightsBusy ? (
                <div className="cfo-home-glance-lead is-loading">
                  Reading your month…
                  <span className="cfo-home-spinner" role="status" aria-label="Loading insights" />
                </div>
              ) : insightsError ? (
                <div className="cfo-home-glance-lead">
                  We hit a snag reading your books — the detail below is still available.
                </div>
              ) : (
                <>
                  <div className="cfo-home-glance-lead">
                    Net income{' '}
                    <span className={`gl-${glance.tone}`}>
                      {glance.dirWord} {glance.pctText}
                      {glance.dirWord === 'held at' ? '' : 'to '}
                      {glance.display}
                    </span>
                    {attentionCount > 0 ? (
                      <>
                        {' '}— <b>{attentionCount} thing{attentionCount === 1 ? '' : 's'}</b> need attention.
                      </>
                    ) : (
                      <> — you’re on track this month.</>
                    )}
                  </div>
                  {glance.chips.length ? (
                    <div className="cfo-home-glance-chips">
                      {glance.chips.map((card) => (
                        <button
                          key={card.id}
                          type="button"
                          className={`cfo-home-attn-chip ${severityDotClass(card.severity)}`}
                          title={card.headline}
                          onClick={() => handleExplain(card)}
                        >
                          <span className="pdot" aria-hidden="true" />
                          <span className="plabel">{insightChipLabel(card)}</span>
                          <span className="pn num">{formatImpact(card)}</span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </>
              )}
            </div>

            <figure className="figure">
              <div className="fig-head">
                <span className="ftitle">What changed — the net-income bridge</span>
                <span className="fmeta cfo-home-bridge-meta">
                  <span className="cfo-home-bridge-window">{bridgeComparison.shortLabel}</span>
                  <span className="cfo-home-bridge-range">{bridge ? bridgeRangeLabel : 'Not enough data'}</span>
                </span>
              </div>
              {bridge ? (
                <CfoInteractiveChart
                  interactiveChildren
                  hint="Hover a bar for its value · click a driver to analyze it"
                  ariaLabel="Explore the net income bridge"
                  onAnalyze={() => openNetIncomeBridge()}
                >
                  <WaterfallBridge
                    className="fig-svg"
                    ariaLabel={`Net income bridge for ${bridgeComparison.label} by driver`}
                    start={{ label: 'Prior NI', value: bridge.priorNetIncome }}
                    steps={[
                      { label: 'Revenue', delta: bridge.revenueDelta },
	                      { label: 'COGS', delta: -bridgeCogsCostDelta },
                      { label: 'Opex', delta: -bridge.expenseDelta },
                    ]}
                    end={{ label: 'This NI', value: bridge.latestNetIncome }}
                    formatValue={(v) => formatCurrencyCompact(v)}
                    interactive
                    onSelect={(sel) => {
                      // Anchors (Prior/This NI) and background clicks → the bridge
                      // in general; a driver bar → analyze THAT driver's own metric
                      // (revenue / COGS / opex), not net income.
                      if (sel.kind === 'element' && !/\bNI\b/i.test(sel.label)) {
                        const driver = sel.label
                        const meta = resolveBridgeDriver(driver)
                        const metricRow = findMetricRow(comparisons, meta.metricId) || findMetricRow(localBridgeComparisons, meta.metricId)
                        const metricComparison = pickComparison(metricRow, comparisonKind)
                        const metricDelta = metricComparison?.delta_abs ?? null
                        const netIncomeEffect =
                          sel.value ?? (metricDelta == null ? 0 : /cost|expense|opex/i.test(meta.metricId) ? -metricDelta : metricDelta)
                        const displayValue = netIncomeEffect
                        analysis.openAnalysis({
                          metricId: meta.metricId,
                          metricLabel: meta.label,
                          comparison: comparisonKind,
                          sourceComponent: 'net_income_bridge_bar',
                          sourceTables: ['fg_cfo_ai_statement_monthly_activity'],
                          selection: {
                            intent: 'explain_selected_change',
                            source_grain: 'bridge_driver',
                            metric_id: meta.metricId,
                            metric_label: meta.label,
                            driver,
                            delta: displayValue,
                            metric_delta: metricDelta,
                            net_income_effect: netIncomeEffect,
                            // Sibling Revenue/COGS/Opex decomposition + Prior→This NI anchors ride
                            // along for the AGENT's bridge context only. The instant card skips
                            // them for bridge_driver clicks (they would repeat the waterfall the
                            // user just clicked); the analysis modal fetches the true next level
                            // (segment contribution / statement categories) into the card instead.
                            drivers: bridgeDrivers,
                            selected_driver_id: meta.metricId,
                            bridge_anchors: bridgeAnchors,
                            comparison_kind: comparisonKind,
                            comparison_label: bridgeComparison.label,
                            prior_period_start: bridge.priorPeriodStart,
                            prior_period_end: bridge.priorPeriodEnd,
                            current_period_start: bridge.latestPeriodStart,
                            current_period_end: bridge.latestPeriodEnd,
                            instant_card_trend: buildInstantMetricTrend(metricRow, meta.label),
                            instant_card_comparisons: buildInstantMetricComparisons(metricRow),
                            instant_card_next_steps: [
                              {
                                kind: 'drilldown',
                                label: `Break down ${meta.label}`,
                                prompt: `Break down ${meta.label} for ${bridgeComparison.label} and show only the largest account drivers.`,
                                target: { surface: SURFACE, metricId: meta.metricId },
                              },
                              {
                                kind: 'compare',
                                label: 'Compare periods',
                                prompt: `Compare ${meta.label} across current month, last 3 months, YTD, and prior year.`,
                                target: { surface: SURFACE, metricId: meta.metricId },
                              },
                            ],
                          },
                          label: meta.label,
                          visual: {
                            kind: 'value',
                            label: `${meta.label} — ${bridgeComparison.shortLabel} net-income effect`,
                            display: `${displayValue >= 0 ? '+' : '−'}${formatCurrencyCompact(Math.abs(displayValue))}`,
                            sub: metricDelta == null
                              ? `${bridgeComparison.shortLabel}: contribution to the selected net-income bridge change · ${meta.evidenceSub}`
                              : `${bridgeComparison.shortLabel}: line movement ${metricDelta >= 0 ? '+' : '−'}${formatCurrencyCompact(Math.abs(metricDelta))} · ${meta.evidenceSub}`,
                            value: displayValue,
                          },
                          seedPrompt: `${meta.seedPrompt} Use the selected comparison window: ${bridgeComparison.label} (${bridgeRangeLabel}).`,
                        })
                      } else {
                        openNetIncomeBridge()
                      }
                    }}
                  />
                </CfoInteractiveChart>
              ) : (
                <p className="cfo-home-figure-empty">Not enough comparable history to draw this bridge for {bridgeComparison.label}.</p>
              )}
              {bridge && bridgeRead?.status === 'ready' && bridgeRead.text ? (
                <div className="airead">
                  <div>{bridgeRead.text}</div>
                </div>
              ) : bridge && bridgeRead?.status === 'loading' ? (
                <div className="airead is-loading">
                  <div>Reading the bridge…</div>
                </div>
              ) : null}
            </figure>

            <div className="kpis" aria-busy={comparisonsBusy} aria-live="polite">
            {comparisonsBusy
              ? KPI_DEFS.map((def) => (
                  <div className="kpi is-loading" key={def.id} aria-hidden="true">
                    <div className="lab">{def.label}</div>
                    <div className="cfo-home-kpi-row">
                      <div className="cfo-home-skel cfo-home-skel-val" />
                      <div className="cfo-home-skel cfo-home-skel-spark" />
                    </div>
                    <div className="cfo-home-skel cfo-home-skel-delta" />
                  </div>
                ))
              : comparisonsError
                ? KPI_DEFS.map((def) => (
                    <div className="kpi is-error" key={def.id}>
                      <div className="lab">{def.label}</div>
                      <div className="val num">—</div>
                      <span className="trend flat">unavailable</span>
                    </div>
                  ))
                : kpis.map((kpi) => {
                    const gradientId = `cfo-home-kpi-spark-${kpi.def.id.replace(/[^a-z0-9_-]/gi, '-')}`
                    const sparkToneClass = kpi.direction === 'down' ? 'is-neg' : kpi.direction === 'up' ? 'is-pos' : 'is-flat'
                    return (
                      <CfoExplainableValue
                        key={kpi.def.id}
                        hint={`Ask AI to explain ${kpi.def.label}`}
                        ariaLabel={`Analyze ${kpi.def.label}`}
                        onAnalyze={() =>
                          analysis.openAnalysis({
                            metricId: kpi.def.id,
                            metricLabel: kpi.def.label,
                            sourceComponent: 'kpi_tile',
                            selection: {
                              source_grain: 'home_kpi',
                              metric_id: kpi.def.id,
                              metric_label: kpi.def.label,
                              comparison_kind: comparisonKind,
                              comparison_label: bridgeComparison.label,
                              instant_card_trend: buildInstantMetricTrend(kpi.row, kpi.def.label),
                              instant_card_comparisons: buildInstantMetricComparisons(kpi.row),
                              instant_card_insights: [
                                kpi.deltaPct != null
                                  ? `${kpi.def.label} is ${formatSignedPct(kpi.deltaPct)} for ${bridgeComparison.label}.`
                                  : `${kpi.def.label} is ${kpi.display} for the selected month.`,
                              ],
                              instant_card_prescriptive_actions: [
                                `Use the comparison table to decide whether ${kpi.def.label.toLowerCase()} needs a drilldown this month.`,
                              ],
                              instant_card_next_steps: [
                                {
                                  kind: 'drilldown',
                                  label: `Break down ${kpi.def.label}`,
                                  prompt: `Break down ${kpi.def.label} and show the largest drivers only.`,
                                  target: { surface: SURFACE, metricId: kpi.def.id },
                                },
                                {
                                  kind: 'compare',
                                  label: 'Compare periods',
                                  prompt: `Compare ${kpi.def.label} across current month, last 3 months, YTD, and prior year.`,
                                  target: { surface: SURFACE, metricId: kpi.def.id },
                                },
                              ],
                            },
                            visual: {
                              kind: 'value',
                              label: kpi.def.label,
                              display: kpi.display,
                              sub:
                                kpi.deltaPct != null
                                  ? `${formatSignedPct(kpi.deltaPct)} ${bridgeComparison.label}`
                                  : kpi.deltaAbs != null
                                    ? `${formatCurrencyCompact(kpi.deltaAbs)} ${bridgeComparison.label}`
                                    : null,
                            },
                          })
                        }
                      >
                      <div className="kpi">
                        <div className="lab">{kpi.def.label}</div>
                        <div className="cfo-home-kpi-row">
                          <div className="val num">{kpi.display}</div>
                          {kpi.sparkline ? (
                            <svg
                              className={`spark ${sparkToneClass}`}
                              viewBox="0 0 100 34"
                              preserveAspectRatio="none"
                              aria-hidden="true"
                            >
                              <defs>
                                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0" stopColor="currentColor" stopOpacity="0.22" />
                                  <stop offset="1" stopColor="currentColor" stopOpacity="0" />
                                </linearGradient>
                              </defs>
                              {kpi.sparkline.zeroY != null ? (
                                <line
                                  className="spark-zero"
                                  x1="3"
                                  y1={kpi.sparkline.zeroY}
                                  x2="97"
                                  y2={kpi.sparkline.zeroY}
                                />
                              ) : null}
                              <path className="spark-area" d={kpi.sparkline.areaPath} fill={`url(#${gradientId})`} />
                              <path className="spark-line" d={kpi.sparkline.linePath} />
                              <circle className="spark-dot" cx={kpi.sparkline.lastX} cy={kpi.sparkline.lastY} r="2.4" />
                            </svg>
                          ) : null}
                        </div>
                        <span className={`trend ${kpi.direction}`}>
                          {kpi.direction === 'up' ? '▲ ' : kpi.direction === 'down' ? '▼ ' : '◦ '}
                          {kpi.deltaPct != null
                            ? `${formatSignedPct(kpi.deltaPct)} ${bridgeComparison.shortLabel}`
                            : kpi.deltaAbs != null
                              ? `${formatCurrencyCompact(kpi.deltaAbs)} ${bridgeComparison.shortLabel}`
                              : `no ${bridgeComparison.shortLabel} comparison`}
                        </span>
                      </div>
                      </CfoExplainableValue>
                    )
                  })}
          </div>

          </section>
        </li>

          {comparisonsError ? (
            <div className="cfo-home-error" role="alert">
              {comparisonsError}
            </div>
          ) : null}

        <li className="step rise" style={{ animationDelay: '.1s' }}>
          <span className="node" aria-hidden="true">
            <b>02</b>
          </span>
          <div className="step-head">
            <span className="step-meta">Ranked by impact</span>
          </div>

          <section className="cards" aria-busy={insightsBusy} aria-live="polite">
            {insightsBusy ? (
              <>
                <div className="cfo-home-skeleton cfo-home-skeleton-progress">
                  <span className="cfo-home-spinner cfo-home-spinner-sm" aria-hidden="true" />
                  Ranking your insights…
                </div>
                {[0, 1, 2].map((i) => (
                  <article className="card edge is-loading" key={`skel-${i}`} aria-hidden="true">
                    <div className="body">
                      <span className="cfo-home-skel cfo-home-skel-pill" />
                      <div className="grow">
                        <div className="cfo-home-skel cfo-home-skel-line cfo-home-skel-line-lg" />
                        <div className="cfo-home-skel cfo-home-skel-line" />
                      </div>
                      <div className="cfo-home-skel cfo-home-skel-impact" />
                    </div>
                  </article>
                ))}
              </>
            ) : insightsError ? (
              <div className="cfo-home-error" role="alert">
                {insightsError}
              </div>
            ) : insights && insights.cards.length > 0 ? (
              <>
                {/* Picture first, then the ranked prose. A dedicated section
                    rather than a chart on every card: inline would put up to
                    seven charts on a calm page and would bypass the up-to-three
                    diversity governance the library exists to enforce. */}
                {briefHighlights.highlights.length > 0 ? (
                  <KeyHighlightsSection
                    highlights={briefHighlights.highlights}
                    evidence={briefHighlights.evidence}
                  />
                ) : null}
                {insights.cards.map((card, idx) => {
                const traceId = traceIdForCard(card.id)
                const sev = card.severity
                const explainer = explainerForCard(card)
                const band = healthBandFromCard(card)
                const valueText = normalizeCurrencyTextCompact(trimStr(card.value_display) || band?.display || formatImpact(card))
                const whatLine = normalizeCurrencyTextCompact(explainer.whatItIs)
                const whyLine = normalizeCurrencyTextCompact(explainer.whyItMatters || card.body)
                return (
                  <article className={`card edge ${sev}${idx === 0 ? ' lead' : ''}`} key={card.id}>
                    <div className="body">
                      <span className="sev">{SEVERITY_LABEL[sev]}</span>
                      <div className="grow">
                        <div className="cfo-home-card-head">
                          <h3>{normalizeCurrencyTextCompact(explainer.friendlyName)}</h3>
                          {explainer.category ? (
                            <span className="cfo-home-card-cat">{explainer.category}</span>
                          ) : null}
                        </div>
                        {whatLine ? <p className="cfo-home-card-what">{whatLine}</p> : null}
                        {band ? (
                          <HealthRangeBar
                            className="cfo-home-card-gauge"
                            value={band.value}
                            healthyMin={band.healthyMin}
                            healthyMax={band.healthyMax}
                            display={valueText}
                            assessment={band.assessment}
                            size="compact"
                            ariaLabel={`${explainer.friendlyName}: ${valueText} against its healthy range`}
                          />
                        ) : null}
                        {whyLine ? <p className="cfo-home-card-why">{whyLine}</p> : null}
                        {explainer.replacementLabel ? (
                          <p className="cfo-home-card-swap">
                            We focus on <b>{explainer.replacementLabel}</b> instead — a better fit for this business.
                          </p>
                        ) : null}
                      </div>
                      <div className="figc">
                        <b className="num">{valueText}</b>
                        {explainer.goodRange ? (
                          <span className="cfo-home-card-goodrange">healthy: {normalizeCurrencyTextCompact(explainer.goodRange)}</span>
                        ) : (
                          <span className="lbl">{card.impact.metric_id ? card.impact.metric_id.replace(/_/g, ' ') : ''}</span>
                        )}
                      </div>
                    </div>
                    <div className="actions">
                      <button type="button" className="btn primary" onClick={() => handleExplain(card)}>
                        <Sparkles size={13} /> Explain
                      </button>
                      <button type="button" className="btn" onClick={() => handleShowDetail(card)}>
                        Show detail
                      </button>
                      <button
                        type="button"
                        className="btn ghost"
                        onClick={() => handleAddToReport(card)}
                        disabled={addedCards.has(card.id)}
                      >
                        {addedCards.has(card.id) ? '✓ Added to report' : '＋ Add to report'}
                      </button>
                      <div className="fb">
                        <AiFeedbackControl aiTraceId={traceId} surface={cfoInsightSurface(SURFACE)} workbookId={workbookId} />
                        <ReportProblemButton
                          surface={cfoInsightSurface(SURFACE)}
                          aiTraceId={traceId}
                          context={{ card_id: card.id, detector: card.detector }}
                        />
                      </div>
                    </div>
                  </article>
                )
                })}
              </>
            ) : (
              <div className="cfo-home-skeleton">No insight cards for this period yet.</div>
            )}
          </section>
        </li>

        <li className="step rise" style={{ animationDelay: '.18s' }}>
          <span className="node" aria-hidden="true">
            <b>03</b>
          </span>
          <div className="step-head">
            <span className="step-meta">Guided journeys</span>
          </div>
          <div className="journeys">
            {GUIDED_JOURNEYS.map((journey) => (
              <button
                type="button"
                className={`journey${getJourneyDefinition(journey.id) ? '' : ' soon'}`}
                key={journey.id}
                onClick={() => {
                  const def = getJourneyDefinition(journey.id)
                  analytics.track({
                    event: 'cfo_home_journey_open',
                    surface: SURFACE,
                    workbook_id: workbookId || null,
                    props: { journey: journey.id, available: Boolean(def) },
                  })
                  if (def) {
                    setActiveJourneyId(journey.id)
                  } else if (journey.id === 'valuation') {
                    // No guided-journey overlay yet: open the Ratios & Valuation
                    // workspace inside CFO Core via the shared deep-link.
                    onOpenCfoCore?.({ tab: 'ratios_valuation', view: null, params: {} })
                  }
                }}
              >
                <div className="top">
                  <div className="jic">{journey.icon}</div>
                  <div>
                    <b>{journey.title}</b>
                    <small>{journey.subtitle}</small>
                  </div>
                </div>
                <div className="prog" aria-hidden="true">
                  <i style={{ width: getJourneyDefinition(journey.id) ? '66%' : '18%' }} />
                </div>
                <div className="meta">
                  <span>{getJourneyDefinition(journey.id) ? 'Ready' : 'Soon'}</span>
                  <span>{getJourneyDefinition(journey.id) ? 'Guided' : 'Queued'}</span>
                </div>
              </button>
            ))}
          </div>
        </li>
      </ol>
    </CfoPageShell>
  )
}

function formatAsOf(asOf: string | null): string {
  if (!asOf) return 'Latest month'
  const parsed = new Date(`${asOf}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return asOf
  return parsed.toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

function formatBridgePeriod(start: string | null | undefined, end: string | null | undefined): string {
  const cleanStart = trimStr(start)
  const cleanEnd = trimStr(end)
  if (!cleanStart && !cleanEnd) return 'Selected period'
  if (!cleanStart || cleanStart === cleanEnd) return formatAsOf(cleanEnd || cleanStart)
  return `${formatAsOf(cleanStart)}–${formatAsOf(cleanEnd)}`
}

export default CfoHomeBrief
